import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { getSocket, isSocketConnected, setUploadsInFlight } from '../socket';
import { uploadFile, compressMediaForChat } from '../firebase';
import { DEFAULT_AVATAR, getDefaultAvatarWithInitial } from '../utils/defaultAvatar';
import { useFormattedTimestamp, useFormattedLastSeen, formatLastSeen } from '../utils/timestampFormatter';
import GroupSettings from './GroupSettings';
import ThreeDViewer from './ThreeDViewer';
import InlineThreeDViewer from './InlineThreeDViewer';
import TenorGifPicker from './TenorGifPicker';
import './ChatWindow.css';
import EmojiPicker from 'emoji-picker-react';
import { DEFAULT_BUBBLE_COLOR, getBubbleHex, hexToRgb, isValidBubbleColor, mixHex, rgba } from '../utils/bubbleColors';
import { debugLog } from '../utils/debug';

let richContentPluginInitPromise = null;

// Simple URL detection for messages that are only a link
const URL_ONLY_REGEX = /^(https?:\/\/|www\.)\S+$/i;

const isProbablyUrl = (text) => URL_ONLY_REGEX.test((text || '').trim());

// Legacy bucket markers (soft-block): treat these URLs as unavailable in UI.
const LEGACY_STORAGE_MARKERS = (() => {
  try {
    const raw = process.env.REACT_APP_LEGACY_STORAGE_MARKERS || '';
    return raw.split(',').map(s => s.trim()).filter(Boolean);
  } catch {
    return [];
  }
})();
const isLegacyStorageUrl = (url) => {
  if (!LEGACY_STORAGE_MARKERS.length) return false;
  const s = String(url || '');
  return LEGACY_STORAGE_MARKERS.some((m) => s.includes(m));
};
const safeImageSrc = (src, fallback = DEFAULT_AVATAR, username = null) => {
  if (!src || src === DEFAULT_AVATAR) return username ? getDefaultAvatarWithInitial(username) : fallback;
  if (isLegacyStorageUrl(src)) return username ? getDefaultAvatarWithInitial(username) : fallback;
  return src;
};

// Component for live updating timestamps
const TimestampDisplay = ({ timestamp, messageId }) => {
  const formattedTime = useFormattedTimestamp(timestamp);
  return <span>{formattedTime}</span>;
};

// Presence label for the chat header: "Online" while connected, otherwise a live
// "last seen Xm ago" (with the device it was last seen on, e.g. "· Mob").
const LastSeenStatus = ({ lastSeen, platform }) => {
  const label = useFormattedLastSeen(lastSeen);
  if (!label) return <span className="chat-members offline">Offline</span>;
  const device = platform === 'mobile' ? ' · Mob' : (platform === 'web' ? ' · PC' : '');
  return <span className="chat-members offline last-seen">{label}{device}</span>;
};

// Component to show read-receipt avatars (user avatars)
// NOTE: We render these at the current read location for each user (their latest seen message).
const SeenByIndicator = ({ userIds, user, room, friends, myBubbleColor, includeSelf = true, maxAvatars = 5 }) => {
  debugLog('SeenByIndicator render:', { userIds, userId: user.uid, room: room?.roomId });

  if (!Array.isArray(userIds) || userIds.length === 0) return null;

  const cleaned = userIds.filter(Boolean);
  const filtered = includeSelf ? cleaned : cleaned.filter((id) => id !== user.uid);
  const unique = Array.from(new Set(filtered));

  if (unique.length === 0) return null;

  // Get user info for those who have seen it
  // In a group chat, we might have room data with members
  const getSeenUserInfo = (userId) => {
    // If it's the current user, use real profile info (you're always "online" to yourself).
    if (userId === user.uid) {
      return {
        id: userId,
        name: user.displayName || user.username || user.email || 'You',
        photo: user.photoURL || DEFAULT_AVATAR,
        bubbleColor: myBubbleColor,
        online: true
      };
    }

    // First check if it's in friends list
    if (friends && Array.isArray(friends)) {
      const friend = friends.find(f => f.userId === userId);
      if (friend) {
        return {
          id: userId,
          name: friend.displayName || friend.username || friend.name || 'User',
          photo: friend.photoURL || DEFAULT_AVATAR,
          bubbleColor: friend.bubbleColor,
          online: !!friend.online,
          lastSeen: friend.lastSeen || null
        };
      }
    }

    // If not in friends, return basic info (presence unknown → treat as offline)
    return {
      id: userId,
      name: 'User',
      photo: DEFAULT_AVATAR,
      bubbleColor: null,
      online: false
    };
  };

  // Prefer consistent order: others first, current user last
  unique.sort((a, b) => {
    if (a === user.uid) return 1;
    if (b === user.uid) return -1;
    return String(a).localeCompare(String(b));
  });

  const seenUsers = unique.slice(0, maxAvatars).map(getSeenUserInfo);
  const hasMore = unique.length > maxAvatars;

  return (
    <div className="seen-by-container" title={`Read by: ${unique.map(id => {
      const info = getSeenUserInfo(id);
      return info.online ? `${info.name} (online)` : info.name;
    }).join(', ')}`}>
      {seenUsers.map((userInfo, idx) => {
        const key =
          userInfo.id === user.uid
            ? (isValidBubbleColor(myBubbleColor) ? myBubbleColor : DEFAULT_BUBBLE_COLOR)
            : (isValidBubbleColor(userInfo.bubbleColor) ? userInfo.bubbleColor : DEFAULT_BUBBLE_COLOR);
        const ring = mixHex(getBubbleHex(key), '#1A1D21', 0.5);
        return (
          <span
            key={userInfo.id}
            className="seen-avatar-wrap"
            style={{ position: 'relative', display: 'inline-block', marginLeft: idx > 0 ? '-8px' : '0' }}
          >
            <img
              src={safeImageSrc(userInfo.photo, DEFAULT_AVATAR, userInfo.name)}
              alt={userInfo.name}
              className="seen-avatar"
              style={{ borderColor: rgba(ring, 0.85) }}
              title={userInfo.online
                ? `${userInfo.name} (online)`
                : (formatLastSeen(userInfo.lastSeen) ? `${userInfo.name} — ${formatLastSeen(userInfo.lastSeen)}` : userInfo.name)}
              onError={(e) => {
                // Avoid broken image icon; fall back to default avatar with initial.
                e.currentTarget.onerror = null;
                e.currentTarget.src = getDefaultAvatarWithInitial(userInfo.name);
              }}
            />
            {/* Read receipts are permanent, so the avatar only means "read this". A live green
                dot is shown only when the person is actually online right now, so a stale
                receipt no longer implies presence. */}
            {userInfo.online && (
              <span
                className="seen-avatar-online-dot"
                title="Online now"
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#22C55E',
                  border: '2px solid #1A1D21',
                  boxSizing: 'content-box',
                  pointerEvents: 'none'
                }}
              />
            )}
          </span>
        );
      })}
      {hasMore && (
        <div className="seen-avatar-more" title={`+${unique.length - maxAvatars} more`}>
          +{unique.length - maxAvatars}
        </div>
      )}
    </div>
  );
};

const normalizeUrl = (url) => {
  const t = (url || '').trim();
  if (!/^https?:\/\//i.test(t)) return `https://${t}`;
  return t;
};

const getHost = (href) => {
  try {
    const host = new URL(href).host || '';
    return host.replace(/^www\./i, '') || href;
  } catch {
    return href;
  }
};

// Link bubble with favicon + fallbacks
function LinkBubble({ href, host, text, styleVars }) {
  const candidates = useMemo(() => {
    const list = [];
    if (host) list.push(`https://icons.duckduckgo.com/ip3/${host}.ico`);
    if (host) list.push(`https://www.google.com/s2/favicons?domain=${host}&sz=64`);
    try {
      const u = new URL(href);
      list.push(`${u.origin}/favicon.ico`);
    } catch {}
    return list;
  }, [host, href]);

  const [index, setIndex] = useState(0);
  const [showIcon, setShowIcon] = useState(false);
  const src = candidates[index];

  return (
    <div className="message-bubble link-bubble clickable" style={styleVars}>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="link-bubble-link"
        title={text}
        onClick={(e) => e.stopPropagation()}
      >
        {!showIcon && src ? (
          <img
            className="link-favicon"
            src={src}
            alt=""
            onError={() => {
              const next = index + 1;
              if (next < candidates.length) {
                setIndex(next);
              } else {
                setShowIcon(true);
              }
            }}
          />
        ) : (
          <span className="material-symbols-outlined link-icon">link</span>
        )}
        <div className="link-text">
          <div className="link-host">{host}</div>
          <div className="link-url">{text}</div>
        </div>
      </a>
    </div>
  );
}

function ChatWindow({ user, room, messages, isLoadingMessages = false, friends, myBubbleColor, onBack, onLoadOlderMessages, loadingOlder, hasMoreOlder, hasMoreNewer = false, jumpTarget = null, onJumpToMessage, onJumpHandled, onReturnToLatest }) {
  const [messageText, setMessageText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [activeUploads, setActiveUploads] = useState(new Set()); // Track individual upload IDs
  const [uploadProgress, setUploadProgress] = useState({}); // uploadId → { stage: 'compressing'|'uploading', percent, originalSize, compressedSize }

  // Tell the socket layer when uploads are in progress so its heartbeat watchdog won't
  // recreate the socket mid-upload and drop the pending message:send (lost media bug).
  useEffect(() => {
    setUploadsInFlight(activeUploads.size > 0);
    return () => setUploadsInFlight(false);
  }, [activeUploads]);
  // Map of username -> live character count of what they're typing
  const [typingUsers, setTypingUsers] = useState(new Map());
  const [isConnected, setIsConnected] = useState(true);
  // Banner is debounced: brief socket blips (heartbeat watchdog recreating the
  // socket, transient network hiccups) reconnect within a few seconds and
  // shouldn't flash "Reconnecting" at the user. Only show after a sustained outage.
  const [showReconnectBanner, setShowReconnectBanner] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState(null); // Now stores { url, message }
  const [fullscreenVideo, setFullscreenVideo] = useState(null); // { url, fileName, message }
  const [show3DViewer, setShow3DViewer] = useState(null); // For 3D model viewer
  const [pendingCompressFiles, setPendingCompressFiles] = useState(null); // { filesWithTypes: [{ file, fileType }], source: 'select'|'drop'|'paste' }
  const [expanded3DModels, setExpanded3DModels] = useState(new Set()); // Track which 3D models are expanded inline
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const mediaPanelContentRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const prevRoomIdRef = useRef(null);
  const [visibleTimestamps, setVisibleTimestamps] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [showActionsFor, setShowActionsFor] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const fullscreenImageRef = useRef(null);
  const imageContextMenuRef = useRef(null);
  const textContextMenuRef = useRef(null);
  const reactionPickerRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const prependAdjustRef = useRef(null); // { prevScrollHeight, prevScrollTop }
  const lastLoadOlderAtRef = useRef(0);
  const messagesRef = useRef(messages);
  const [reactionMenu, setReactionMenu] = useState(null); // { x, y, messageId }
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pickerMessageId, setPickerMessageId] = useState(null);
  const [replyTo, setReplyTo] = useState(null); // { messageId, senderId, senderName, type, content }
  // Persist recently-used emojis for quick access (used across sessions).
  const loadRecentlyUsedEmojis = () => {
    try {
      const saved = localStorage.getItem('recentlyUsedEmojis');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };
  const [imageContextMenu, setImageContextMenu] = useState(null); // { x, y, messageId, mediaUrl, fileName }
  const [textContextMenu, setTextContextMenu] = useState(null); // { x, y, messageId }
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const shouldAutoScrollRef = useRef(true);
  const previousMessageCountRef = useRef(0);
  const [showGroupSettings, setShowGroupSettings] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const [showMediaPanel, setShowMediaPanel] = useState(() => {
    const saved = localStorage.getItem('mediaPanelOpen');
    return saved !== null ? saved === 'true' : false;
  });
  const [mediaPanelTab, setMediaPanelTab] = useState(() => {
    const saved = localStorage.getItem('mediaPanelTab');
    return (saved === 'media' || saved === 'stats') ? saved : 'media';
  });
  const [fullscreenMetadata, setFullscreenMetadata] = useState({});
  const [isHoveringFullscreen, setIsHoveringFullscreen] = useState(false);
  const [showFullscreenMetadata, setShowFullscreenMetadata] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [serverStats, setServerStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const statsFetchedAtRef = useRef(null); // { roomId, timestamp }

  // Save media panel state to localStorage
  useEffect(() => {
    localStorage.setItem('mediaPanelOpen', showMediaPanel.toString());
  }, [showMediaPanel]);
  useEffect(() => {
    localStorage.setItem('mediaPanelTab', mediaPanelTab);
  }, [mediaPanelTab]);

  // Fetch full chat stats from server when stats tab is opened or room changes
  // Caches for 60s to avoid hammering the server on repeated tab switches
  useEffect(() => {
    if (!showMediaPanel || mediaPanelTab !== 'stats' || !room?.roomId) return;
    const socket = getSocket();
    if (!socket || !isSocketConnected()) return;

    // Skip fetch if we already have fresh stats for this room (< 60s old)
    const cached = statsFetchedAtRef.current;
    if (cached && cached.roomId === room.roomId && serverStats && (Date.now() - cached.timestamp) < 60000) {
      return;
    }

    setStatsLoading(true);
    socket.emit('room:stats:get', { roomId: room.roomId });

    const handleStats = ({ roomId: rid, stats }) => {
      if (rid === room.roomId) {
        setServerStats(stats);
        setStatsLoading(false);
        statsFetchedAtRef.current = { roomId: rid, timestamp: Date.now() };
      }
    };
    socket.on('room:stats', handleStats);
    return () => {
      socket.off('room:stats', handleStats);
    };
  }, [showMediaPanel, mediaPanelTab, room?.roomId]);

  // Clear cached stats when switching rooms
  useEffect(() => {
    setServerStats(null);
    statsFetchedAtRef.current = null;
  }, [room?.roomId]);

  // Message search handler
  useEffect(() => {
    if (!showSearch) return;
    const socket = getSocket();
    if (!socket) return;

    const handleSearchResults = ({ results, total, query: q }) => {
      if (q === searchQuery.trim()) {
        setSearchResults(results || []);
        setSearchTotal(total || 0);
        setSearchLoading(false);
      }
    };
    socket.on('message:search:results', handleSearchResults);
    return () => {
      socket.off('message:search:results', handleSearchResults);
    };
  }, [showSearch, searchQuery]);

  // Debounced search emit
  useEffect(() => {
    if (!showSearch) return;
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      setSearchTotal(0);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const socket = getSocket();
      if (socket && isSocketConnected()) {
        socket.emit('message:search', { query: trimmed, roomId: room?.roomId || null, limit: 50 });
      }
    }, 350);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery, showSearch, room?.roomId]);

  // Close search on room change
  useEffect(() => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);
  }, [room?.roomId]);

  // Ctrl+F opens search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && room) {
        e.preventDefault();
        setShowSearch(prev => !prev);
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
      if (e.key === 'Escape' && showSearch) {
        setShowSearch(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [room, showSearch]);

  const handleSearchResultClick = (result) => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults([]);

    const targetRoomId = result.roomId || room?.roomId;
    const inCurrentRoom = targetRoomId === room?.roomId;
    const msgElement = inCurrentRoom ? document.getElementById(`msg_${result.id}`) : null;

    if (msgElement) {
      // Already rendered — just scroll to it.
      focusMessageElement(msgElement);
      return;
    }

    // Not loaded (older page) or in another chat — ask Chat to load the window around it.
    if (typeof onJumpToMessage === 'function') {
      onJumpToMessage(targetRoomId, result.id);
    }
  };

  // Compute "current read location" chips: for each user, find the latest message they have seen,
  // then render their avatar only at that message.
  const readReceiptUserIdsByMessageId = useMemo(() => {
    const list = Array.isArray(messages) ? messages : [];

    const participantSet = new Set();
    (Array.isArray(room?.members) ? room.members : []).forEach((id) => id && participantSet.add(id));
    if (user?.uid) participantSet.add(user.uid);

    // Also include anyone we learn about from message payloads (seenBy/sender), to be robust.
    list.forEach((m) => {
      if (m?.senderId) participantSet.add(m.senderId);
      (Array.isArray(m?.seenBy) ? m.seenBy : []).forEach((id) => id && participantSet.add(id));
      Object.keys(m?.seenAt || {}).forEach((id) => id && participantSet.add(id));
    });

    const lastIndexByUserId = new Map(); // userId -> latest message index they have seen

    list.forEach((m, idx) => {
      if (!m) return;

      const seenSet = new Set(Array.isArray(m.seenBy) ? m.seenBy : []);

      // Treat the sender as having seen their own message (even if backend doesn't add them to seenBy).
      if (m.senderId) seenSet.add(m.senderId);

      // Some payloads also track seenAt per user; include those keys.
      Object.keys(m.seenAt || {}).forEach((uid) => seenSet.add(uid));

      seenSet.forEach((uid) => {
        if (!uid) return;
        if (!participantSet.has(uid)) return;
        lastIndexByUserId.set(uid, idx);
      });
    });

    const byMessageId = new Map(); // messageId -> [userIds]
    for (const [uid, idx] of lastIndexByUserId.entries()) {
      const mid = list[idx]?.id;
      if (!mid) continue;
      const arr = byMessageId.get(mid) || [];
      arr.push(uid);
      byMessageId.set(mid, arr);
    }

    // Ensure stable, unique arrays
    for (const [mid, arr] of byMessageId.entries()) {
      const uniq = Array.from(new Set(arr.filter(Boolean)));
      uniq.sort((a, b) => {
        if (a === user?.uid) return 1;
        if (b === user?.uid) return -1;
        return String(a).localeCompare(String(b));
      });
      byMessageId.set(mid, uniq);
    }

    return byMessageId;
  }, [messages, room?.members, user?.uid]);

  // Typing indicators must be scoped to the current room only.
  // Clear any prior room's typing state when switching rooms so it can't "leak" into the next chat.
  useEffect(() => {
    const nextRoomId = room?.roomId || null;
    const prevRoomId = prevRoomIdRef.current;

    // If we were typing in the previous room, stop it so others don't see us typing forever.
    if (prevRoomId && prevRoomId !== nextRoomId) {
      try {
        const socket = getSocket();
        if (socket && isSocketConnected()) {
          socket.emit('typing:stop', { roomId: prevRoomId });
        }
      } catch (_) {
        // ignore
      }
    }

    // Clear local timers + UI state on any room change (including to/from null).
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    setTypingUsers(new Map());
    prevRoomIdRef.current = nextRoomId;
  }, [room?.roomId]);

  const LegacyStorageNotice = ({ fileName }) => (
    <div className="media-moved-notice">
      <div className="media-moved-title">Media unavailable</div>
      <div className="media-moved-text">
        This media is no longer available. It may have been moved or deleted from storage.
      </div>
      {fileName ? <div className="media-moved-filename">{fileName}</div> : null}
    </div>
  );
  
  // Common reaction emojis like Facebook Messenger
  const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '😡', '👏'];

  // Quick reactions: show last used (newest -> oldest), falling back to defaults.
  const getQuickReactions = () => {
    const recent = loadRecentlyUsedEmojis();
    const out = [];
    const seen = new Set();

    const push = (e) => {
      if (!e) return;
      if (seen.has(e)) return;
      seen.add(e);
      out.push(e);
    };

    recent.forEach(push);
    REACTION_EMOJIS.forEach(push);

    // Keep the row compact.
    return out.slice(0, 7);
  };

  const isMobile = () => window.matchMedia && window.matchMedia('(max-width: 768px)').matches;

  // Update recently used emojis
  const updateRecentlyUsed = (emoji) => {
    const prev = loadRecentlyUsedEmojis();
    const filtered = prev.filter(e => e !== emoji);
    const updated = [emoji, ...filtered].slice(0, 12); // Keep only 12 most recent
    localStorage.setItem('recentlyUsedEmojis', JSON.stringify(updated));
  };

  const openEmojiPicker = (messageId, x, y) => {
    setPickerMessageId(messageId);
    setReactionMenu({ x, y, messageId });
    // Desktop: open compact reaction popover first. Mobile: open full picker.
    setShowEmojiPicker(isMobile());
  };

  // Keep messagesRef in sync so scroll handler doesn't need messages in deps
  messagesRef.current = messages;

  // Smart scroll management that maintains position when images load
  useEffect(() => {
    const messageCount = messages?.length || 0;
    const isNewMessage = messageCount > previousMessageCountRef.current;

    // Only auto-scroll if:
    // 1. It's a new message (not just re-rendering existing ones)
    // 2. User is already near the bottom (hasn't scrolled up to read history)
    if (isNewMessage && shouldAutoScrollRef.current) {
      scrollToBottom();
    }

    previousMessageCountRef.current = messageCount;
  }, [messages]);

  // If we just prepended older messages, preserve the user's viewport position.
  // useLayoutEffect runs synchronously after DOM mutation but BEFORE the browser
  // paints, so the user never sees the scroll jump.
  useLayoutEffect(() => {
    const pending = prependAdjustRef.current;
    if (!pending) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const newScrollHeight = container.scrollHeight;
    const delta = newScrollHeight - pending.prevScrollHeight;
    container.scrollTop = pending.prevScrollTop + delta;
    prependAdjustRef.current = null;
  }, [messages]);

  // Refs for scroll handler deps so the listener doesn't need to be recreated
  const hasMoreOlderRef = useRef(hasMoreOlder);
  const loadingOlderRef = useRef(loadingOlder);
  const onLoadOlderMessagesRef = useRef(onLoadOlderMessages);
  const roomIdRef = useRef(room?.roomId);
  hasMoreOlderRef.current = hasMoreOlder;
  loadingOlderRef.current = loadingOlder;
  onLoadOlderMessagesRef.current = onLoadOlderMessages;
  roomIdRef.current = room?.roomId;

  // Check if user is near bottom to determine if we should auto-scroll.
  // This listener is attached ONCE per room and reads from refs, so it is never
  // torn down / re-attached during normal message flow.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const checkScrollPosition = () => {
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      // Only trigger a React state update when the value actually changes
      if (shouldAutoScrollRef.current !== isNearBottom) {
        shouldAutoScrollRef.current = isNearBottom;
        setShouldAutoScroll(isNearBottom);
      }

      // Pagination: load older messages when reaching top
      if (
        container.scrollTop < 50 &&
        hasMoreOlderRef.current &&
        !loadingOlderRef.current &&
        typeof onLoadOlderMessagesRef.current === 'function'
      ) {
        const now = Date.now();
        if (now - lastLoadOlderAtRef.current < 700) return; // basic throttle
        lastLoadOlderAtRef.current = now;
        const msgs = messagesRef.current;
        const oldest = msgs && msgs.length ? msgs[0] : null;
        const beforeTs = oldest?.timestamp;
        if (beforeTs) {
          prependAdjustRef.current = { prevScrollHeight: container.scrollHeight, prevScrollTop: container.scrollTop };
          onLoadOlderMessagesRef.current(roomIdRef.current, beforeTs);
        }
      }
    };

    container.addEventListener('scroll', checkScrollPosition, { passive: true });
    return () => container.removeEventListener('scroll', checkScrollPosition);
  }, [room?.roomId]); // Only re-attach when switching rooms

  // Listen for keyboard GIF insertion (Android)
  useEffect(() => {
    const initRichContentPlugin = async () => {
      try {
        // Web should never try to initialize this plugin.
        const isNative =
          typeof window !== 'undefined' &&
          window.Capacitor &&
          typeof window.Capacitor.getPlatform === 'function' &&
          window.Capacitor.getPlatform() !== 'web';
        if (!isNative) return null;

        // Only initialize once per page load to avoid "already registered" errors.
        if (richContentPluginInitPromise) return richContentPluginInitPromise;
        richContentPluginInitPromise = (async () => {
          const { registerPlugin } = await import('@capacitor/core');
          const RichContent = registerPlugin('RichContent');
        
          // Add listener for keyboard GIF insertion
          const listener = await RichContent.addListener('keyboardGifInserted', async (data) => {
          if (!room || activeUploads.size > 0) {
            return;
          }
          
          const uploadId = `gif_upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const placeholderId = `placeholder_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

          try {
            // Add to active uploads
            setActiveUploads(prev => new Set([...prev, uploadId]));
            const socket = getSocket();
            if (!socket || !isSocketConnected()) {
              alert('Connection lost. Please try again.');
              return;
            }
            
            // Create placeholder message immediately (hide Tenor/keyboard labels)
            const extension = data.mimeType ? data.mimeType.split('/')[1] || 'gif' : 'gif';
            const fileName = `gif_${Date.now()}.${extension}`;
            const placeholderMessage = {
              id: placeholderId,
              senderId: user.uid,
              senderName: user.displayName,
              senderPhoto: user.photoURL,
              senderBubbleColor: isValidBubbleColor(myBubbleColor) ? myBubbleColor : DEFAULT_BUBBLE_COLOR,
              content: '',
              type: 'gif',
              mediaUrl: null, // Will be null until upload completes
              fileName: fileName,
              fileSize: null,
              timestamp: Date.now(),
              roomId: room.roomId,
              isPlaceholder: true // Flag to indicate this is a placeholder
            };
            
            // Emit placeholder to show immediately in chat
            console.log('📱🎬 Emitting GIF placeholder message:', placeholderMessage);
            socket.emit('message:placeholder', placeholderMessage);
            
            // Convert base64 to blob
            const base64Data = data.data;
            const mimeType = data.mimeType || 'image/gif';
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });
            
            // Create a file from the blob
            const file = new File([blob], fileName, { type: mimeType });
            
            // Upload to Firebase
            const filePath = `chat-media/${room.roomId}/${Date.now()}_${fileName}`;
            const downloadUrl = await uploadFile(file, filePath);
            
            // Send the actual message with media URL, replacing the placeholder
            socket.emit('message:send', {
              roomId: room.roomId,
              content: '',
              type: 'gif',
              mediaUrl: downloadUrl,
              fileName: fileName,
              fileSize: file.size,
              replacePlaceholder: placeholderId // Tell server to replace this placeholder
            });
          } catch (error) {
            console.error('❌ Error processing keyboard GIF:', error);
            alert('Failed to send GIF. Please try again.');
            
            // Remove placeholder on error
            const socket = getSocket();
            if (socket && isSocketConnected()) {
              socket.emit('message:placeholder:remove', { placeholderId, roomId: room?.roomId });
            }
          } finally {
            // Remove from active uploads
            setActiveUploads(prev => {
              const newSet = new Set(prev);
              newSet.delete(uploadId);
              return newSet;
            });
          }
        });
        
          await RichContent.initialize();
          debugLog('RichContent plugin initialized');
        
          // Cleanup listener on unload only; component mounts can vary by room selection.
          window.addEventListener('beforeunload', () => {
            try { listener.remove(); } catch (_) {}
          });
        })().catch((err) => {
          // Swallow native-only plugin errors so they don't become unhandled rejections.
          debugLog('RichContent plugin init failed:', err);
          richContentPluginInitPromise = null;
          return null;
        });
        return richContentPluginInitPromise;
      } catch (error) {
        debugLog('RichContent plugin not available (web or iOS):', error);
        richContentPluginInitPromise = null;
      }
    };
    
    const cleanup = initRichContentPlugin();
    return () => {
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then(cleanupFn => cleanupFn && cleanupFn());
      }
    };
  }, [room, uploading]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    // Check connection status
    const checkConnection = () => {
      setIsConnected(isSocketConnected());
    };

    // Check connection status periodically
    const connectionInterval = setInterval(checkConnection, 5000);
    checkConnection(); // Initial check

    const typingHandler = ({ userId, username, roomId, typing, count }) => {
      if (roomId === room?.roomId && userId !== user.uid) {
        setTypingUsers((prev) => {
          const newMap = new Map(prev);
          if (typing) {
            newMap.set(username, count || 0);
          } else {
            newMap.delete(username);
          }
          return newMap;
        });
      }
    };
    socket.on('typing:user', typingHandler);

    // Clear typing indicators when users go offline
    const friendOfflineHandler = (data) => {
      const { userId } = data || {};
      if (!userId) return;
      
      // Find the username from friends list to remove from typing
      const offlineFriend = friends?.find(f => f.userId === userId);
      if (offlineFriend) {
        setTypingUsers((prev) => {
          const newMap = new Map(prev);
          newMap.delete(offlineFriend.username || offlineFriend.displayName || offlineFriend.name);
          return newMap;
        });
      }
    };
    socket.on('friend:offline', friendOfflineHandler);

    const connectHandler = () => {
      setIsConnected(true);
    };
    socket.on('connect', connectHandler);

    const disconnectHandler = () => {
      setIsConnected(false);
      setTypingUsers(new Map()); // Clear typing indicators on disconnect
    };
    socket.on('disconnect', disconnectHandler);

    return () => {
      clearInterval(connectionInterval);
      socket.off('typing:user', typingHandler);
      socket.off('friend:offline', friendOfflineHandler); // Remove only OUR handler
      socket.off('connect', connectHandler);
      socket.off('disconnect', disconnectHandler);
    };
  }, [room, user.uid, friends]);

  // Only surface the reconnecting banner after the connection has been down
  // for a sustained period; hide it immediately once we're back.
  useEffect(() => {
    if (isConnected) {
      setShowReconnectBanner(false);
      return;
    }
    const timer = setTimeout(() => setShowReconnectBanner(true), 10000);
    return () => clearTimeout(timer);
  }, [isConnected]);

  // Handle seen status updates
  useEffect(() => {
    const socket = getSocket();
    if (!socket || !room) return;

    const handleMessageSeen = (data) => {
      console.log('👁️ Message seen update:', data);
      // This will be handled by the parent component to update the messages state
      // We'll emit a custom event that the parent can listen to
      window.dispatchEvent(new CustomEvent('messageSeen', { detail: data }));
    };

    const handleMessageReaction = (data) => {
      console.log('👍 Reaction update:', data);
      // Emit custom event for parent to update messages
      window.dispatchEvent(new CustomEvent('messageReaction', { detail: data }));
      // Do not auto-scroll for reactions; preserve user's scroll position
    };

    socket.on('message:seen', handleMessageSeen);
    socket.on('message:reaction', handleMessageReaction);

    return () => {
      socket.off('message:seen', handleMessageSeen);
      socket.off('message:reaction', handleMessageReaction);
    };
  }, [room]);

  // Scroll to bottom function
  const scrollToBottom = () => {
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      const container = messagesContainerRef.current;
      if (container) {
        // Jump immediately to bottom (no smooth animation)
        container.scrollTop = container.scrollHeight;
      }
    });
  };

  // Debounced version to avoid jumpiness when many images/videos finish loading.
  const scrollDebounceRef = useRef(null);
  const initialBottomLockRef = useRef(false);
  const initialBottomLockTimerRef = useRef(null);
  const bottomLockRafRef = useRef(null);
  const bottomLockUntilRef = useRef(0);
  const bottomLockStableFramesRef = useRef(0);

  const stopBottomLock = () => {
    initialBottomLockRef.current = false;
    bottomLockUntilRef.current = 0;
    bottomLockStableFramesRef.current = 0;
    if (bottomLockRafRef.current) {
      cancelAnimationFrame(bottomLockRafRef.current);
      bottomLockRafRef.current = null;
    }
  };

  const startBottomLock = (durationMs = 1200) => {
    const container = messagesContainerRef.current;
    if (!container) return;

    initialBottomLockRef.current = true;
    bottomLockUntilRef.current = Date.now() + durationMs;
    bottomLockStableFramesRef.current = 0;

    if (bottomLockRafRef.current) {
      cancelAnimationFrame(bottomLockRafRef.current);
      bottomLockRafRef.current = null;
    }

    const tick = () => {
      const c = messagesContainerRef.current;
      if (!c) {
        stopBottomLock();
        return;
      }

      const prevTop = c.scrollTop;
      c.scrollTop = c.scrollHeight;

      if (c.scrollTop === prevTop) {
        bottomLockStableFramesRef.current += 1;
      } else {
        bottomLockStableFramesRef.current = 0;
      }

      const now = Date.now();
      const timeUp = now >= bottomLockUntilRef.current;
      const stableEnough = bottomLockStableFramesRef.current >= 6;
      if (timeUp && stableEnough) {
        stopBottomLock();
        return;
      }

      bottomLockRafRef.current = requestAnimationFrame(tick);
    };

    bottomLockRafRef.current = requestAnimationFrame(tick);
  };
  const scheduleScrollToBottom = (delayMs = 120) => {
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
      scrollDebounceRef.current = null;
    }
    scrollDebounceRef.current = setTimeout(() => {
      scrollToBottom();
    }, delayMs);
  };

  useEffect(() => {
    return () => {
      if (scrollDebounceRef.current) {
        clearTimeout(scrollDebounceRef.current);
        scrollDebounceRef.current = null;
      }
      if (initialBottomLockTimerRef.current) {
        clearTimeout(initialBottomLockTimerRef.current);
        initialBottomLockTimerRef.current = null;
      }
      stopBottomLock();
    };
  }, []);

  // When switching rooms, lock scroll-to-bottom briefly while media hydrates.
  useEffect(() => {
    const rid = room?.roomId || null;
    if (!rid) return;

    shouldAutoScrollRef.current = true;
    setShouldAutoScroll(true);

    if (initialBottomLockTimerRef.current) {
      clearTimeout(initialBottomLockTimerRef.current);
      initialBottomLockTimerRef.current = null;
    }
    // Deterministic bottom lock (no scrollIntoView spam).
    startBottomLock(1400);
  }, [room?.roomId]);

  // When history finishes loading, do a deterministic bottom scroll (prevents random landing).
  useEffect(() => {
    if (!room?.roomId) return;
    if (isLoadingMessages) return;
    if (!messages || messages.length === 0) return;
    // If user hasn't scrolled up, land at bottom.
    if (shouldAutoScrollRef.current || initialBottomLockRef.current) {
      startBottomLock(800);
    }
  }, [room?.roomId, isLoadingMessages, messages?.length]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (reactionMenu) {
        setReactionMenu(null);
        setShowEmojiPicker(false);
      }
      if (imageContextMenu) {
        setImageContextMenu(null);
      }
      if (textContextMenu) {
        setTextContextMenu(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [reactionMenu, imageContextMenu, textContextMenu]);

  // Clamp desktop context menus to viewport after render
  useEffect(() => {
    const clampToViewport = (menuEl, setMenuState, current) => {
      if (!menuEl || !current) return;
      const rect = menuEl.getBoundingClientRect();
      const padding = 8;
      let { x, y } = current;
      if (rect.right > window.innerWidth - padding) {
        x = Math.max(padding, window.innerWidth - rect.width - padding);
      }
      if (rect.bottom > window.innerHeight - padding) {
        y = Math.max(padding, window.innerHeight - rect.height - padding);
      }
      if (x !== current.x || y !== current.y) {
        setMenuState({ ...current, x, y });
      }
    };

    if (!isMobile()) {
      clampToViewport(imageContextMenuRef.current, setImageContextMenu, imageContextMenu);
      clampToViewport(textContextMenuRef.current, setTextContextMenu, textContextMenu);
    }
  }, [imageContextMenu, textContextMenu]);

  // Clamp reaction menus / emoji picker to viewport after render (desktop)
  useEffect(() => {
    if (isMobile()) return;

    const clampFixed = (menuEl, current) => {
      if (!menuEl || !current) return current;
      const rect = menuEl.getBoundingClientRect();
      const padding = 8;
      let { x, y } = current;
      if (rect.right > window.innerWidth - padding) x = Math.max(padding, window.innerWidth - rect.width - padding);
      if (rect.bottom > window.innerHeight - padding) y = Math.max(padding, window.innerHeight - rect.height - padding);
      if (x < padding) x = padding;
      if (y < padding) y = padding;
      return (x === current.x && y === current.y) ? current : { ...current, x, y };
    };

    if (reactionMenu && !showEmojiPicker) {
      const next = clampFixed(reactionPickerRef.current, reactionMenu);
      if (next !== reactionMenu) setReactionMenu(next);
    }
    if (reactionMenu && showEmojiPicker) {
      const next = clampFixed(emojiPickerRef.current, reactionMenu);
      if (next !== reactionMenu) setReactionMenu(next);
    }
  }, [reactionMenu, showEmojiPicker]);

  const shouldScrollForMessage = (messageId) => {
    if (shouldAutoScrollRef.current) return true;
    if (!messages || messages.length === 0) return false;
    const index = messages.findIndex(m => m.id === messageId);
    if (index === -1) return false;
    return index >= Math.max(0, messages.length - 3);
  };

  // Keep bottom stable during media loads.
  const handleMediaLoad = () => {
    if (initialBottomLockRef.current || shouldAutoScrollRef.current) startBottomLock(500);
  };

  // Mark messages as seen when they come into view
  const markMessageAsSeen = (messageId) => {
    if (!room) return;
    
    const socket = getSocket();
    if (!socket || !isSocketConnected()) {
      console.warn('⚠️ Socket not connected, cannot mark message as seen');
      return;
    }

    try {
      socket.emit('message:seen', { roomId: room.roomId, messageId });
    } catch (error) {
      console.error('❌ Error marking message as seen:', error);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim() || !room) return;

    const socket = getSocket();
    if (!socket || !isSocketConnected()) {
      console.error('❌ Socket not connected, cannot send message');
      alert('Connection lost. Please check your internet connection and try again.');
      return;
    }

    try {
      // Mark all unread messages from others as seen when sending a message
      // (if you're replying, you've clearly seen the messages)
      messages.forEach(msg => {
        if (msg.senderId !== user.uid && !msg.seenBy?.includes(user.uid)) {
          console.log('📖 Marking message as seen before sending:', msg.id);
          markMessageAsSeen(msg.id);
        }
      });

      socket.emit('message:send', {
        roomId: room.roomId,
        content: messageText,
        type: 'text',
        replyTo: replyTo ? {
          messageId: replyTo.messageId,
          senderId: replyTo.senderId,
          senderName: replyTo.senderName,
          type: replyTo.type,
          content: replyTo.content,
          mediaUrl: replyTo.mediaUrl || null
        } : null
      });

      setMessageText('');
      setReplyTo(null);
      // Clear contenteditable if using it
      if (messageInputRef.current) {
        messageInputRef.current.innerText = '';
      }
      socket.emit('typing:stop', { roomId: room.roomId });
    } catch (error) {
      console.error('❌ Error sending message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const startReply = (message) => {
    if (!message) return;
    const content = (message.content || '').trim();
    const preview = content
      ? content.slice(0, 140)
      : (message.type === 'image' ? '[Image]'
        : message.type === 'gif' ? '[GIF]'
        : message.type === 'video' ? '[Video]'
        : message.type === 'file' ? '[File]'
        : `[${message.type || 'message'}]`);

    setReplyTo({
      messageId: message.id,
      senderId: message.senderId,
      senderName: message.senderName || 'User',
      type: message.type || 'text',
      content: preview,
      mediaUrl: message.mediaUrl || null
    });

    // Close any open menus
    setShowEmojiPicker(false);
    setReactionMenu(null);
    setTextContextMenu(null);
    setImageContextMenu(null);

    requestAnimationFrame(() => {
      messageInputRef.current?.focus?.();
    });
  };

  // Scroll a message into view and flash it. Cancels any pending auto-scroll-to-bottom,
  // otherwise the bottom lock immediately drags the view back down.
  const recenterTimersRef = useRef([]);
  const lastHighlightedRef = useRef(null);
  const focusMessageElement = (el, { smooth = true } = {}) => {
    if (!el) return;
    if (lastHighlightedRef.current && lastHighlightedRef.current !== el) {
      lastHighlightedRef.current.classList.remove('search-highlight');
    }
    lastHighlightedRef.current = el;
    shouldAutoScrollRef.current = false;
    setShouldAutoScroll(false);
    initialBottomLockRef.current = false;
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
      scrollDebounceRef.current = null;
    }
    stopBottomLock();
    recenterTimersRef.current.forEach(clearTimeout);
    recenterTimersRef.current = [];

    el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });

    el.classList.remove('search-highlight');
    // Force a reflow so the animation restarts if the same message is targeted twice.
    void el.offsetWidth;
    el.classList.add('search-highlight');
    recenterTimersRef.current.push(setTimeout(() => el.classList.remove('search-highlight'), 2000));

    // Media above the target can finish loading after the jump and push it off screen.
    // Re-centre a couple of times, but bail out the moment the user scrolls themselves.
    if (!smooth) {
      const container = messagesContainerRef.current;
      if (!container) return;
      let expectedTop = container.scrollTop;
      [250, 700].forEach((delay) => {
        recenterTimersRef.current.push(setTimeout(() => {
          const c = messagesContainerRef.current;
          if (!c || !el.isConnected) return;
          if (Math.abs(c.scrollTop - expectedTop) > 4) return; // user took over
          const elRect = el.getBoundingClientRect();
          const cRect = c.getBoundingClientRect();
          const delta = (elRect.top + elRect.height / 2) - (cRect.top + cRect.height / 2);
          const next = Math.max(0, Math.min(c.scrollTop + delta, c.scrollHeight - c.clientHeight));
          if (Math.abs(next - c.scrollTop) < 8) return;
          c.scrollTop = next;
          expectedTop = c.scrollTop;
        }, delay));
      });
    }
  };

  useEffect(() => () => {
    recenterTimersRef.current.forEach(clearTimeout);
    recenterTimersRef.current = [];
  }, []);

  const scrollToMessage = (messageId) => {
    if (!messageId) return;
    const el = document.getElementById(`msg_${messageId}`);
    if (el) {
      focusMessageElement(el);
      return;
    }
    // Replied-to message is older than the loaded page — load the window around it.
    if (typeof onJumpToMessage === 'function' && room?.roomId) {
      onJumpToMessage(room.roomId, messageId);
    }
  };

  // Handle a pending jump request: once the target message is in the loaded list, scroll
  // to it. Retries across a few frames because media/layout can still be settling.
  const handledJumpRef = useRef(null);
  useEffect(() => {
    const targetId = jumpTarget?.messageId;
    if (!targetId) return;

    const jumpKey = `${targetId}:${jumpTarget?.at || ''}`;
    if (handledJumpRef.current === jumpKey) return;
    if (!Array.isArray(messages) || !messages.some((m) => m?.id === targetId)) return;

    handledJumpRef.current = jumpKey;

    let attempts = 0;
    let cancelled = false;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(`msg_${targetId}`);
      if (el) {
        // Jumping into freshly loaded history can be thousands of pixels away — a smooth
        // scroll there is slow and disorienting, so land on it directly.
        focusMessageElement(el, { smooth: !!jumpTarget?.inView });
        if (typeof onJumpHandled === 'function') onJumpHandled(room?.roomId);
        return;
      }
      if (attempts++ < 20) {
        requestAnimationFrame(tryScroll);
      } else if (typeof onJumpHandled === 'function') {
        onJumpHandled(room?.roomId);
      }
    };
    requestAnimationFrame(tryScroll);

    return () => { cancelled = true; };
  }, [jumpTarget?.messageId, jumpTarget?.at, messages, room?.roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  const getUserDisplayName = (userId) => {
    if (!userId) return 'User';
    if (userId === user.uid) return 'You';
    const friend = Array.isArray(friends) ? friends.find(f => f.userId === userId) : null;
    return friend?.displayName || friend?.username || friend?.name || 'User';
  };

  const reactionTitle = (emoji, userIds) => {
    const ids = Array.isArray(userIds) ? userIds : [];
    const names = ids.map(getUserDisplayName);
    const list = names.slice(0, 10).join(', ');
    const more = names.length > 10 ? ` (+${names.length - 10} more)` : '';
    return `${emoji} ${names.length} - ${list}${more}`;
  };

  const handleTyping = (e) => {
    const text = e.target.value || e.target.innerText;
    setMessageText(text);
    
    if (!room) return;
    
    const socket = getSocket();
    if (!socket || !isSocketConnected()) {
      console.warn('⚠️ Socket not connected, skipping typing indicator');
      return;
    }

    try {
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      if (text.trim().length > 0) {
        // Only emit typing:start if there's actually text.
        // Send the live character count so others see how much has been typed.
        socket.emit('typing:start', { roomId: room.roomId, count: text.length });

        // Keep the "is typing" indicator alive through short pauses — thinking
        // mid-sentence, or browsing the OS/mobile emoji keyboard (which fires no
        // input events). The receiver doesn't auto-expire, so this grace period
        // is what controls how long the indicator stays up after the last keystroke.
        typingTimeoutRef.current = setTimeout(() => {
          if (socket && isSocketConnected()) {
            socket.emit('typing:stop', { roomId: room.roomId });
          }
        }, 8000);
      } else {
        // If text is empty, immediately stop typing indicator
        socket.emit('typing:stop', { roomId: room.roomId });
      }
    } catch (error) {
      console.error('❌ Error sending typing indicator:', error);
    }
  };

  const handleContentEditableInput = (e) => {
    const text = e.currentTarget.innerText;
    setMessageText(text);
    handleTyping({ target: { innerText: text } });
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items || !room) return;

    const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB

    let hasImage = false;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if the pasted item is an image or GIF
      if (item.type.indexOf('image') !== -1) {
        hasImage = true;
        e.preventDefault(); // Prevent default paste behavior
        
        const file = item.getAsFile();
        if (!file) continue;

        // Check file size
        if (file.size > MAX_FILE_SIZE) {
          alert(`Pasted image is too large. Maximum file size is 200MB.`);
          continue;
        }

        // Determine file type
        let fileType = file.type === 'image/gif' ? 'gif' : 'image';
        
        // For pasted images (not GIFs), show compression dialog
        if (fileType === 'image') {
          setPendingCompressFiles({ filesWithTypes: [{ file, fileType }], source: 'paste' });
          continue;
        }

        // For GIFs, upload directly without compression
        const uploadId = `paste_upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Add to active uploads
        setActiveUploads(prev => new Set([...prev, uploadId]));
        
        try {
          const fileExtension = file.type.split('/')[1] || 'gif';
          const safeFileName = `${fileType}_${Date.now()}.${fileExtension}`;
          // Upload to Firebase Storage
          const filePath = `chat-media/${room.roomId}/${Date.now()}_${safeFileName}`;
          const downloadUrl = await uploadFile(file, filePath);

          // Send message with media
          const socket = getSocket();
          if (!socket || !isSocketConnected()) {
            console.error('❌ Socket not connected, cannot send image');
            alert('Connection lost. Please check your internet connection and try again.');
            return;
          }

          socket.emit('message:send', {
            roomId: room.roomId,
            content: '',
            type: fileType,
            mediaUrl: downloadUrl,
            fileName: safeFileName,
            fileSize: file.size
          });
          
          // Clear the input
          if (messageInputRef.current) {
            messageInputRef.current.innerText = '';
            setMessageText('');
          }
        } catch (error) {
          console.error('Error uploading pasted image:', error);
          alert('Failed to upload image. Please try again.');
        } finally {
          // Remove from active uploads
          setActiveUploads(prev => {
            const newSet = new Set(prev);
            newSet.delete(uploadId);
            return newSet;
          });
        }
      }
    }
    
    // If we handled an image, don't let text through
    if (hasImage) {
      e.preventDefault();
      return;
    }

    // Force plaintext paste (avoid auto-styled links)
    try {
      const plain = e.clipboardData?.getData('text/plain');
      if (plain && messageInputRef.current && (!uploading && activeUploads.size === 0)) {
        e.preventDefault();
        // Insert as plain text at cursor
        const insertText = (text) => {
          // execCommand is deprecated but broadly supported across Chromium/Firefox
          const success = document.execCommand('insertText', false, text);
          if (!success) {
            // Fallback: replace selection range
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return;
            const range = selection.getRangeAt(0);
            range.deleteContents();
            range.insertNode(document.createTextNode(text));
            // Move caret to end of inserted text
            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        };
        insertText(plain);
        const newText = messageInputRef.current.innerText || '';
        setMessageText(newText);
        handleTyping({ target: { innerText: newText } });
      }
    } catch (err) {
      console.warn('Plaintext paste failed, falling back to default:', err);
    }
  };

  const handleGifSelect = async (gifUrl /* gifTitle unused: avoid sending Tenor description */) => {
    if (!gifUrl || !room) return;

    const uploadId = `gif_upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    // AI-generated clips are animated WebP; Tenor results are GIF. Preserve the
    // correct extension/mime so the animation plays back properly.
    const isWebp = /\.webp(\?|$)/i.test(gifUrl);
    const mediaExt = isWebp ? 'webp' : 'gif';
    const mediaMime = isWebp ? 'image/webp' : 'image/gif';
    const fileName = `gif_${Date.now()}.${mediaExt}`;
    // Declared outside the try so the catch block can clean up the placeholder on failure.
    const placeholderId = `placeholder_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Add to active uploads
      setActiveUploads(prev => new Set([...prev, uploadId]));
      const socket = getSocket();
      if (!socket || !isSocketConnected()) {
        alert('Connection lost. Please try again.');
        return;
      }
      
      // Create placeholder message immediately
      const placeholderMessage = {
        id: placeholderId,
        senderId: user.uid,
        senderName: user.displayName,
        senderPhoto: user.photoURL,
        senderBubbleColor: isValidBubbleColor(myBubbleColor) ? myBubbleColor : DEFAULT_BUBBLE_COLOR,
        content: '',
        type: 'gif',
        mediaUrl: null,
        fileName,
        fileSize: null,
        timestamp: Date.now(),
        roomId: room.roomId,
        isPlaceholder: true
      };
      
      // Emit placeholder to show immediately in chat
      console.log('🎬 Emitting GIF placeholder message:', placeholderMessage);
      socket.emit('message:placeholder', placeholderMessage);

      // Fetch the GIF and convert to blob
      const response = await fetch(gifUrl);
      const blob = await response.blob();
      
      // Create a file from the blob
      const file = new File([blob], fileName, { type: mediaMime });
      
      // Upload to Firebase Storage
      const filePath = `chat-media/${room.roomId}/${Date.now()}_${file.name}`;
      const downloadUrl = await uploadFile(file, filePath);
      
      // Send the actual message with media URL, replacing the placeholder
      socket.emit('message:send', {
        roomId: room.roomId,
        content: '',
        type: 'gif',
        mediaUrl: downloadUrl,
        fileName: file.name,
        fileSize: file.size,
        replacePlaceholder: placeholderId
      });
    } catch (error) {
      console.error('Error sending GIF:', error);
      alert('Failed to send GIF. Please try again.');
      
      // Remove placeholder on error so it doesn't spin forever
      const socket = getSocket();
      if (socket && isSocketConnected()) {
        socket.emit('message:placeholder:remove', { placeholderId, roomId: room.roomId });
      }
    } finally {
      // Remove from active uploads
      setActiveUploads(prev => {
        const newSet = new Set(prev);
        newSet.delete(uploadId);
        return newSet;
      });
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !room) return;

    // Check file size limits (100MB max)
    const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" is too large. Maximum file size is 200MB.`);
        fileInputRef.current.value = '';
        return;
      }
    }

    const buildSafeFileName = (fileType, file) => {
      const nameParts = (file.name || '').split('.');
      const extFromName = nameParts.length > 1 ? nameParts.pop().toLowerCase() : '';
      const extFromType = (file.type || '').split('/')[1] || '';
      const ext = (extFromName || extFromType || 'bin').toLowerCase();
      if (['image', 'gif', 'video'].includes(fileType)) {
        return `${fileType}_${Date.now()}.${ext}`;
      }
      return file.name || `file_${Date.now()}.${ext}`;
    };

    // Determine file types for all files
    const filesWithTypes = files.map(file => {
      let fileType = 'file';
      const fileExtension = file.name.toLowerCase().split('.').pop();
      
      if (file.type === 'image/gif') {
        fileType = 'gif';
      } else if (file.type.startsWith('image/')) {
        fileType = 'image';
      } else if (file.type.startsWith('video/')) {
        fileType = 'video';
      } else if (['obj', 'fbx', 'gltf', 'glb', 'stl', '3ds', 'blend', 'ply'].includes(fileExtension)) {
        fileType = '3d';
      } else if (['pdf'].includes(fileExtension) || file.type === 'application/pdf') {
        fileType = 'pdf';
      } else if (['doc', 'docx'].includes(fileExtension) || 
                 ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)) {
        fileType = 'document';
      } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(fileExtension) || 
                 file.type.includes('zip') || file.type.includes('compress') || file.type.includes('archive')) {
        fileType = 'archive';
      }
      
      return { file, fileType };
    });

    // Check if any files are compressible (images/videos, not GIFs)
    const hasCompressible = filesWithTypes.some(({ fileType }) => ['image', 'video'].includes(fileType));

    if (hasCompressible) {
      // Show compression dialog — let user choose
      setPendingCompressFiles({ filesWithTypes, source: 'select' });
    } else {
      // No compressible files, upload directly
      const uploadPromises = filesWithTypes.map(async ({ file, fileType }) => {
        return await uploadMediaFile(file, fileType, false);
      });
      try {
        await Promise.allSettled(uploadPromises);
      } finally {
        fileInputRef.current.value = '';
      }
    }
  };

  // Drag-and-drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set false when actually leaving the container, not entering a child
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0 || !room) return;

    const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" is too large. Maximum file size is 200MB.`);
        return;
      }
    }

    const filesWithTypes = files.map(file => {
      let fileType = 'file';
      const fileExtension = file.name.toLowerCase().split('.').pop();

      if (file.type === 'image/gif') {
        fileType = 'gif';
      } else if (file.type.startsWith('image/')) {
        fileType = 'image';
      } else if (file.type.startsWith('video/')) {
        fileType = 'video';
      } else if (['obj', 'fbx', 'gltf', 'glb', 'stl', '3ds', 'blend', 'ply'].includes(fileExtension)) {
        fileType = '3d';
      } else if (['pdf'].includes(fileExtension) || file.type === 'application/pdf') {
        fileType = 'pdf';
      } else if (['doc', 'docx'].includes(fileExtension) ||
                 ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)) {
        fileType = 'document';
      } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(fileExtension) ||
                 file.type.includes('zip') || file.type.includes('compress') || file.type.includes('archive')) {
        fileType = 'archive';
      }

      return { file, fileType };
    });

    const hasCompressible = filesWithTypes.some(({ fileType }) => ['image', 'video'].includes(fileType));

    if (hasCompressible) {
      setPendingCompressFiles({ filesWithTypes, source: 'drop' });
    } else {
      const uploadPromises = filesWithTypes.map(async ({ file, fileType }) => {
        return await uploadMediaFile(file, fileType, false);
      });
      await Promise.allSettled(uploadPromises);
    }
  };

  // Extracted upload logic to be reused by both direct upload and compression dialog
  const uploadMediaFile = async (file, fileType, shouldCompress = false) => {
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const placeholderId = `placeholder_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Add to active uploads
      setActiveUploads(prev => new Set([...prev, uploadId]));

      const buildSafeFileName = (fileType, file) => {
        const nameParts = (file.name || '').split('.');
        const extFromName = nameParts.length > 1 ? nameParts.pop().toLowerCase() : '';
        const extFromType = (file.type || '').split('/')[1] || '';
        const ext = (extFromName || extFromType || 'bin').toLowerCase();
        if (['image', 'gif', 'video'].includes(fileType)) {
          return `${fileType}_${Date.now()}.${ext}`;
        }
        return file.name || `file_${Date.now()}.${ext}`;
      };

      const safeFileName = buildSafeFileName(fileType, file);
      const contentForMessage = ['image', 'gif', 'video'].includes(fileType) ? '' : safeFileName;
      const socket = getSocket();
      if (!socket || !isSocketConnected()) {
        console.error('❌ Socket not connected, cannot send file');
        alert('Connection lost. Please check your internet connection and try again.');
        return;
      }
      
      // Create placeholder message immediately
      const placeholderMessage = {
        id: placeholderId,
        senderId: user.uid,
        senderName: user.displayName,
        senderPhoto: user.photoURL,
        senderBubbleColor: isValidBubbleColor(myBubbleColor) ? myBubbleColor : DEFAULT_BUBBLE_COLOR,
        content: contentForMessage,
        type: fileType,
        mediaUrl: null, // Will be null until upload completes
        fileName: safeFileName,
        fileSize: file.size,
        timestamp: Date.now(),
        roomId: room.roomId,
        isPlaceholder: true, // Flag to indicate this is a placeholder
        uploadId: uploadId // Track progress for this upload
      };

      // Emit placeholder to show immediately in chat
      console.log('📱🔄 Emitting placeholder message:', placeholderMessage);
      socket.emit('message:placeholder', placeholderMessage);

      // Compress if requested; videos are ALWAYS normalized to a desktop-playable
      // codec even when the user picked "Full Res" — an HEVC/.mov clip left as-is
      // plays on the Android app but shows a dead control bar on desktop web.
      let finalFile = file;
      const isVideo = (file.type || '').startsWith('video/');
      if (fileType !== 'gif' && (shouldCompress || isVideo)) {
        try {
          if (isVideo) {
            setUploadProgress(prev => ({ ...prev, [uploadId]: { stage: 'compressing', percent: 0, originalSize: file.size } }));
          }
          finalFile = await compressMediaForChat(file, {
            forceCompress: shouldCompress, // size compression is opt-in; codec normalization always runs
            onProgress: isVideo ? (pct) => {
              setUploadProgress(prev => ({ ...prev, [uploadId]: { ...prev[uploadId], stage: 'compressing', percent: pct } }));
            } : undefined
          });
          if (finalFile !== file) {
            setUploadProgress(prev => ({ ...prev, [uploadId]: { ...prev[uploadId], compressedSize: finalFile.size } }));
          }
          console.log(`📦 Processed ${file.name}: ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(finalFile.size / 1024 / 1024).toFixed(2)}MB`);
        } catch (err) {
          console.error('Compression failed, using original:', err);
          finalFile = file;
        }
      }

      // Upload to Firebase Storage with progress tracking
      setUploadProgress(prev => ({ ...prev, [uploadId]: { ...prev[uploadId], stage: 'uploading', percent: 0, originalSize: prev[uploadId]?.originalSize || file.size } }));
      const filePath = `chat-media/${room.roomId}/${Date.now()}_${safeFileName}`;
      const downloadUrl = await uploadFile(finalFile, filePath, {
        // Serve the right MIME type so desktop browsers treat it as playable
        // media (a missing/wrong type — e.g. video/quicktime — makes controls dead).
        contentType: finalFile.type || (fileType === 'video' ? 'video/mp4' : undefined),
        onProgress: (pct) => {
          setUploadProgress(prev => ({ ...prev, [uploadId]: { ...prev[uploadId], stage: 'uploading', percent: pct } }));
        }
      });

      // Send message with media, replacing the placeholder
      try {
        socket.emit('message:send', {
          roomId: room.roomId,
          content: contentForMessage,
          type: fileType,
          mediaUrl: downloadUrl,
          fileName: safeFileName,
          fileSize: finalFile.size, // Use compressed file size
          replacePlaceholder: placeholderId // Tell server to replace this placeholder
        });
      } catch (error) {
        console.error('❌ Error sending file:', error);
        alert(`Failed to send ${file.name}. Please try again.`);
        
        // Remove placeholder on error
        socket.emit('message:placeholder:remove', { placeholderId, roomId: room?.roomId });
      }
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error);
      alert(`Failed to upload ${file.name}. Please try again.`);

      // Remove placeholder on error
      const socket = getSocket();
      if (socket && isSocketConnected()) {
        socket.emit('message:placeholder:remove', { placeholderId, roomId: room?.roomId });
      }
    } finally {
      // Remove from active uploads and progress tracking
      setActiveUploads(prev => {
        const newSet = new Set(prev);
        newSet.delete(uploadId);
        return newSet;
      });
      setUploadProgress(prev => {
        const next = { ...prev };
        delete next[uploadId];
        return next;
      });
    }
  };

  const handleCompressionChoice = async (shouldCompress) => {
    if (!pendingCompressFiles) return;
    const { filesWithTypes, source } = pendingCompressFiles;
    setPendingCompressFiles(null);

    const uploadPromises = filesWithTypes.map(async ({ file, fileType }) => {
      const compressThis = shouldCompress && ['image', 'video'].includes(fileType);
      return await uploadMediaFile(file, fileType, compressThis);
    });

    try {
      await Promise.allSettled(uploadPromises);
    } finally {
      if (source === 'select' && fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatVideoDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDateShort = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const hoursStr = String(hours).padStart(2, '0');
    return `${day}/${month}/${year} ${hoursStr}:${minutes} ${ampm}`;
  };

  const toggleTimestamp = (messageId) => {
    setVisibleTimestamps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const handleReactionClick = (messageId, emoji) => {
    if (!room) return;
    const socket = getSocket();
    if (!socket || !isSocketConnected()) return;

    // Update recently used emojis
    updateRecentlyUsed(emoji);

    socket.emit('message:react', {
      roomId: room.roomId,
      messageId,
      emoji
    });

    setReactionMenu(null);
    setShowEmojiPicker(false);

    // Do not auto-scroll for reactions; preserve user's scroll position
  };

  const handleRightClick = (e, message) => {
    e.preventDefault();
    
    if (message.senderId === user.uid) {
      // Open own text context menu on desktop
      if (!isMobile()) {
        setTextContextMenu({ x: e.clientX, y: e.clientY, messageId: message.id });
      }
      return;
    }
    
    openEmojiPicker(message.id, e.clientX, e.clientY);
  };

  const handleImageRightClick = (e, message) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    
    // For others' media, open full emoji picker instead of context menu
    if (message.senderId !== user.uid) {
      openEmojiPicker(message.id, e.clientX, e.clientY);
      return;
    }
    
    // For own media, show download/delete context menu
    setImageContextMenu({
      x: e.clientX,
      y: e.clientY,
      messageId: message.id,
      mediaUrl: message.mediaUrl,
      fileName: message.fileName,
      messageType: message.type
    });
  };

  // Helper function to toggle 3D model expansion
  const toggle3DModel = (messageId) => {
    setExpanded3DModels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };
  const getFileIcon = (messageType, fileName) => {
    switch (messageType) {
      case 'pdf':
        return { icon: 'picture_as_pdf', color: '#FF4444' };
      case 'document':
        return { icon: 'description', color: '#4285F4' };
      case 'archive':
        return { icon: 'folder_zip', color: '#FF9800' };
      case '3d':
        return { icon: 'view_in_ar', color: '#3BA6FF' };
      case 'file':
      default:
        const extension = fileName?.toLowerCase().split('.').pop();
        if (['txt', 'md'].includes(extension)) {
          return { icon: 'article', color: '#4CAF50' };
        } else if (['json', 'xml', 'csv'].includes(extension)) {
          return { icon: 'code', color: '#607D8B' };
        }
        return { icon: 'description', color: '#3BA6FF' };
    }
  };

  const downloadImage = async (mediaUrl, fileName, messageType) => {
    try {
      const response = await fetch(mediaUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'media_file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
      setImageContextMenu(null);
    } catch (error) {
      console.error('Error downloading media:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const renderMessage = (message, index, allMessages) => {
    const isOwnMessage = message.senderId === user.uid;
    const bubbleKey = (() => {
      if (isOwnMessage) return isValidBubbleColor(myBubbleColor) ? myBubbleColor : DEFAULT_BUBBLE_COLOR;
      if (isValidBubbleColor(message.senderBubbleColor)) return message.senderBubbleColor;
      const friend = Array.isArray(friends) ? friends.find(f => f.userId === message.senderId) : null;
      if (isValidBubbleColor(friend?.bubbleColor)) return friend.bubbleColor;
      return DEFAULT_BUBBLE_COLOR;
    })();
    const bubbleHex = getBubbleHex(bubbleKey);
    // Soften the palette about 50% to reduce intensity in-chat.
    const bubbleSoftHex = mixHex(bubbleHex, '#1A1D21', 0.5);
    const { r, g, b } = hexToRgb(bubbleSoftHex);
    const bubbleVars = {
      '--bubble-bg': rgba(bubbleSoftHex, 0.14),
      '--bubble-border': rgba(bubbleSoftHex, 0.45),
      '--bubble-bg-hover': rgba(bubbleSoftHex, 0.22),
      '--bubble-border-hover': bubbleSoftHex,
      '--reaction-border': rgba(bubbleSoftHex, 0.55),
      '--reaction-border-hover': bubbleSoftHex,
      // OPAQUE reacted background (no transparency)
      '--reaction-bg-reacted': mixHex(bubbleSoftHex, '#1A1D21', 0.60),
      '--reaction-bg': '#1A1D21',
      '--reaction-bg-hover': '#222831',
      '--bubble-accent-rgb': `${r}, ${g}, ${b}`
    };
    
    // Message grouping logic
    const prevMessage = index > 0 ? allMessages[index - 1] : null;
    const nextMessage = index < allMessages.length - 1 ? allMessages[index + 1] : null;
    
    const GROUP_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
    const isGroupedWithPrevious = prevMessage &&
      prevMessage.senderId === message.senderId &&
      (message.timestamp - prevMessage.timestamp) <= GROUP_WINDOW_MS;
    
    const isGroupedWithNext = nextMessage &&
      nextMessage.senderId === message.senderId &&
      (nextMessage.timestamp - message.timestamp) <= GROUP_WINDOW_MS;
    
    const groupPosition = (() => {
      if (isGroupedWithPrevious && isGroupedWithNext) return 'middle';
      if (isGroupedWithPrevious && !isGroupedWithNext) return 'last';
      if (!isGroupedWithPrevious && isGroupedWithNext) return 'first';
      return 'single';
    })();
    
    // Show elements based on group position
    const showProfilePicture = !isOwnMessage && (groupPosition === 'first' || groupPosition === 'single');
    const showSenderName = !isOwnMessage && (groupPosition === 'first' || groupPosition === 'single');
    const showTimestamp = groupPosition === 'last' || groupPosition === 'single';
    const isTimestampVisible = visibleTimestamps.has(message.id);
    // Get seen status for this message
    const seenBy = message.seenBy || [];

    // Who should show a read-receipt avatar at THIS message (latest seen location per user)?
    const readReceiptUserIds = readReceiptUserIdsByMessageId.get(message.id) || [];

    const startEdit = () => {
      setEditingId(message.id);
      setEditDraft(message.content || '');
      setShowActionsFor(null);
    };

    const cancelEdit = () => {
      setEditingId(null);
      setEditDraft('');
    };

    const submitEdit = () => {
      if (!room) return;
      const socket = getSocket();
      if (!socket || !isSocketConnected()) return;
      const content = editDraft.trim();
      if (!content) return;
      socket.emit('message:edit', { roomId: room.roomId, messageId: message.id, content });
      setEditingId(null);
      setEditDraft('');
    };

    const requestDelete = () => {
      if (!room) return;
      if (!window.confirm('Delete this message for everyone?')) return;
      const socket = getSocket();
      if (!socket || !isSocketConnected()) return;
      socket.emit('message:delete', { roomId: room.roomId, messageId: message.id });
      setShowActionsFor(null);
    };

    const textContent = (message.content || '').trim();
    const isLinkOnly = isProbablyUrl(textContent);
    const linkHref = isLinkOnly ? normalizeUrl(textContent) : null;
    const linkHost = isLinkOnly && linkHref ? getHost(linkHref) : null;

    const myKey = isValidBubbleColor(myBubbleColor) ? myBubbleColor : DEFAULT_BUBBLE_COLOR;
    const mySoftHex = mixHex(getBubbleHex(myKey), '#1A1D21', 0.5);

    const softHexForUserId = (uid) => {
      if (!uid) return mixHex(getBubbleHex(DEFAULT_BUBBLE_COLOR), '#1A1D21', 0.5);
      if (uid === user.uid) return mySoftHex;
      const friend = Array.isArray(friends) ? friends.find(f => f.userId === uid) : null;
      const key = isValidBubbleColor(friend?.bubbleColor) ? friend.bubbleColor : DEFAULT_BUBBLE_COLOR;
      return mixHex(getBubbleHex(key), '#1A1D21', 0.5);
    };

    // Slightly less softened for reaction borders so the ring is always visible
    const ringHexForUserId = (uid) => {
      if (!uid) return mixHex(getBubbleHex(DEFAULT_BUBBLE_COLOR), '#1A1D21', 0.35);
      if (uid === user.uid) return mixHex(getBubbleHex(myKey), '#1A1D21', 0.35);
      const friend = Array.isArray(friends) ? friends.find(f => f.userId === uid) : null;
      const key = isValidBubbleColor(friend?.bubbleColor) ? friend.bubbleColor : DEFAULT_BUBBLE_COLOR;
      return mixHex(getBubbleHex(key), '#1A1D21', 0.35);
    };

    const buildBorderGradientForUserIds = (ids) => {
      if (!Array.isArray(ids) || ids.length === 0) {
        const c = mixHex(getBubbleHex(DEFAULT_BUBBLE_COLOR), '#1A1D21', 0.5);
        return { base: c, borderLayer: `linear-gradient(${c}, ${c})` };
      }

      // Weight segments by number of reactors (supports 2 reds + 1 green => 2/3 red, 1/3 green)
      const counts = new Map(); // color -> count
      const order = []; // preserve first-seen order of colors
      ids.forEach((uid) => {
        const c = ringHexForUserId(uid);
        const prev = counts.get(c) || 0;
        if (prev === 0) order.push(c);
        counts.set(c, prev + 1);
      });

      const total = ids.length;
      if (order.length === 1) {
        const c = order[0];
        return { base: c, borderLayer: `linear-gradient(${c}, ${c})` };
      }

      let start = 0;
      const parts = order.map((c) => {
        const count = counts.get(c) || 0;
        const end = start + (count / total) * 100;
        const seg = `${c} ${start.toFixed(4)}% ${end.toFixed(4)}%`;
        start = end;
        return seg;
      });
      // Ensure it ends at 100% (avoid tiny rounding gaps)
      parts[parts.length - 1] = parts[parts.length - 1].replace(/(\d+(\.\d+)?)%\s*$/, '100%');

      return {
        base: order[0],
        borderLayer: `conic-gradient(from var(--reaction-border-angle, 90deg), ${parts.join(', ')})`
      };
    };

    const reactionsEl = (message.reactions && Object.keys(message.reactions).length > 0) ? (
      <div className="message-reactions message-reactions--floating">
        {Object.entries(message.reactions).map(([emoji, userIds]) => (
          (() => {
            const hasReacted = userIds.includes(user.uid);
            // Reaction chip color should represent WHO reacted, not who sent the message.
            const { base: baseHex, borderLayer } = buildBorderGradientForUserIds(userIds);

            const reactionVars = {
              '--reaction-border-layer': borderLayer,
              '--reaction-border-layer-hover': borderLayer,
              '--reaction-fill': '#1A1D21',
              '--reaction-fill-hover': '#222831',
              '--reaction-fill-reacted': mixHex(baseHex, '#1A1D21', 0.60)
            };

            // If YOU reacted, highlight with YOUR color (so your chip stands out to you).
            const finalVars = hasReacted
              ? {
                  ...reactionVars,
                  '--reaction-fill-reacted': mixHex(mySoftHex, '#1A1D21', 0.60)
                }
              : reactionVars;

            return (
              <button
                key={emoji}
                className={`reaction-bubble ${hasReacted ? 'reacted' : ''}`}
                onClick={() => handleReactionClick(message.id, emoji)}
                title={reactionTitle(emoji, userIds)}
                type="button"
                style={finalVars}
              >
                <span className="reaction-emoji">{emoji}</span>
                {userIds.length > 1 && <span className="reaction-count">{userIds.length}</span>}
              </button>
            );
          })()
        ))}
      </div>
    ) : null;

    return (
      <div
        key={message.id}
        className={`message ${isOwnMessage ? 'own' : 'other'} ${groupPosition}`}
        onContextMenu={(e) => handleRightClick(e, message)}
        id={`msg_${message.id}`}
        data-message-id={message.id}
        style={bubbleVars}
        ref={(el) => {
          // Mark message as seen when it comes into view (only for messages from others)
          if (el && !isOwnMessage && !seenBy.includes(user.uid)) {
            const observer = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (entry.isIntersecting) {
                    console.log('👀 Message entered viewport, marking as seen:', message.id);
                    markMessageAsSeen(message.id);
                    observer.disconnect();
                  }
                });
              },
              { threshold: 0.1 } // Lowered from 0.5 to 0.1 - mark as seen when 10% visible
            );
            observer.observe(el);
          }
        }}
      >
        {!isOwnMessage && (
          <div className="message-avatar-container">
            {showProfilePicture ? (
              <img
                src={safeImageSrc(message.senderPhoto, DEFAULT_AVATAR, message.senderName)}
                alt={message.senderName}
                className="message-avatar"
                style={{
                  border: `2px solid ${bubbleSoftHex}`,
                  boxShadow: `0 0 0 2px rgba(13, 15, 18, 0.9)`
                }}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = getDefaultAvatarWithInitial(message.senderName);
                }}
              />
            ) : (
              <div className="message-avatar-spacer"></div>
            )}
          </div>
        )}
        <div className="message-content">
          {showSenderName && (
            <div className="message-sender">{message.senderName}</div>
          )}
          
          {message.type === 'text' && (
            <>
              {editingId === message.id ? (
                <div className="message-bubble" style={{ padding: 0 }}>
                  <textarea
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    rows={Math.min(10, Math.max(3, Math.ceil((editDraft.length || 1) / 34)))}
                    className="edit-textarea"
                    style={{ width: '100%', resize: 'vertical', padding: '12px', borderRadius: '10px', border: '1px solid #2C3036' }}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitEdit(); }
                      if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '8px' }}>
                    <button type="button" onClick={cancelEdit} className="btn btn-text">Cancel</button>
                    <button type="button" onClick={submitEdit} className="btn btn-primary">Save</button>
                  </div>
                </div>
              ) : (
                isLinkOnly ? (
                  <div className="bubble-with-reactions">
                    <LinkBubble href={linkHref} host={linkHost} text={textContent} styleVars={bubbleVars} />
                    {reactionsEl}
                  </div>
                ) : (
                  <div className="bubble-with-reactions">
                    <div 
                      className="message-bubble clickable"
                      style={bubbleVars}
                      onClick={() => {
                        if (isOwnMessage) {
                          setShowActionsFor((prev) => prev === message.id ? null : message.id);
                        } else {
                          toggleTimestamp(message.id);
                        }
                      }}
                      onContextMenu={(e) => handleRightClick(e, message)}
                    >
                      {message.replyTo && (
                        <div
                          className="reply-preview"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            scrollToMessage(message.replyTo.messageId);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.stopPropagation();
                              scrollToMessage(message.replyTo.messageId);
                            }
                          }}
                          title={`Replying to ${message.replyTo.senderName || 'User'}`}
                        >
                          <div className="reply-preview-header">
                            <div>
                              <div className="reply-preview-name">{message.replyTo.senderName || 'User'}</div>
                              <div className="reply-preview-content">{message.replyTo.content}</div>
                            </div>
                            {message.replyTo.mediaUrl && ['image', 'gif', 'video'].includes(message.replyTo.type) && (
                              message.replyTo.type === 'video' ? (
                                <video src={message.replyTo.mediaUrl} className="reply-preview-thumb" muted />
                              ) : (
                                <img src={message.replyTo.mediaUrl} alt="" className="reply-preview-thumb" />
                              )
                            )}
                          </div>
                        </div>
                      )}
                      {message.content}
                    </div>
                    {reactionsEl}
                  </div>
                )
              )}
            </>
          )}

          {(message.type === 'image' || message.type === 'gif') && (
            <div className="bubble-with-reactions">
              <div className="message-media" style={{ position: 'relative', borderColor: bubbleSoftHex }}>
              {message.isPlaceholder ? (
                (() => {
                  const prog = message.uploadId ? uploadProgress[message.uploadId] : null;
                  const stage = prog?.stage;
                  const pct = prog?.percent ?? 0;
                  const label = stage === 'compressing' ? `Compressing... ${pct}%` : stage === 'uploading' ? `Uploading... ${pct}%` : 'Preparing...';
                  const compressionNote = prog?.compressedSize && prog?.originalSize
                    ? `${(prog.originalSize / (1024*1024)).toFixed(0)}MB → ${(prog.compressedSize / (1024*1024)).toFixed(0)}MB`
                    : null;
                  return (
                <div className="media-placeholder" style={{
                  width: '200px',
                  height: '150px',
                  backgroundColor: '#1A1D21',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  border: `2px solid ${bubbleSoftHex}`,
                  boxShadow: `0 0 12px ${rgba(bubbleSoftHex, 0.28)}`
                }}>
                  <span style={{
                    color: bubbleSoftHex,
                    fontSize: '13px',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {label}
                  </span>
                  <div className="upload-progress-bar-track">
                    <div className="upload-progress-bar-fill" style={{ width: `${pct}%`, backgroundColor: bubbleSoftHex }}></div>
                  </div>
                  {compressionNote && (
                    <span style={{ color: '#6BCB77', fontSize: '10px' }}>{compressionNote}</span>
                  )}
                  <span style={{
                    color: '#9A9FA5',
                    fontSize: '10px',
                    textAlign: 'center'
                  }}>
                    {message.fileName}
                  </span>
                </div>
                  );
                })()
              ) : (
                isLegacyStorageUrl(message.mediaUrl) ? (
                  <div
                    onClick={() => isOwnMessage && setShowActionsFor((prev) => prev === message.id ? null : message.id)}
                    onContextMenu={(e) => handleImageRightClick(e, message)}
                    style={{ cursor: isOwnMessage ? 'pointer' : 'default' }}
                  >
                    <LegacyStorageNotice fileName={message.fileName} />
                  </div>
                ) : (
                  <img
                    src={message.mediaUrl}
                    alt={message.fileName}
                    className="message-image"
                    onClick={() => setFullscreenImage({ url: message.mediaUrl, message })}
                    onContextMenu={(e) => handleImageRightClick(e, message)}
                    style={{ cursor: 'pointer' }}
                    loading="lazy"
                    decoding="async"
                    onLoad={handleMediaLoad}
                    onError={(e) => {
                      console.error('Image failed to load:', message.mediaUrl);
                      e.target.style.display = 'none';
                    }}
                  />
                )
              )}
              {/* Delete overlay only on mobile; desktop uses context menu */}
              {isOwnMessage && !message.isPlaceholder && showActionsFor === message.id && isMobile() && (
                <button
                  onClick={requestDelete}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'rgba(225, 29, 72, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '16px'
                  }}
                  title="Delete image"
                >
                  ✕
                </button>
              )}
              </div>
              {reactionsEl}
            </div>
          )}

          {message.type === 'video' && (
            <div className="bubble-with-reactions">
              <div className="message-media" style={{ position: 'relative', borderColor: bubbleSoftHex }}>
              {message.isPlaceholder ? (
                (() => {
                  const prog = message.uploadId ? uploadProgress[message.uploadId] : null;
                  const stage = prog?.stage;
                  const pct = prog?.percent ?? 0;
                  const label = stage === 'compressing' ? `Compressing video... ${pct}%` : stage === 'uploading' ? `Uploading... ${pct}%` : 'Preparing...';
                  const compressionNote = prog?.compressedSize && prog?.originalSize
                    ? `${(prog.originalSize / (1024*1024)).toFixed(0)}MB → ${(prog.compressedSize / (1024*1024)).toFixed(0)}MB`
                    : null;
                  return (
                <div className="media-placeholder" style={{
                  width: '280px',
                  height: '160px',
                  backgroundColor: '#1A1D21',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  border: `2px solid ${bubbleSoftHex}`,
                  boxShadow: `0 0 12px ${rgba(bubbleSoftHex, 0.28)}`
                }}>
                  <span className="material-symbols-outlined" style={{ color: bubbleSoftHex, fontSize: '28px' }}>videocam</span>
                  <span style={{ color: bubbleSoftHex, fontSize: '13px', fontWeight: '600' }}>{label}</span>
                  <div className="upload-progress-bar-track" style={{ width: '80%' }}>
                    <div className="upload-progress-bar-fill" style={{ width: `${pct}%`, backgroundColor: bubbleSoftHex }}></div>
                  </div>
                  {compressionNote && (
                    <span style={{ color: '#6BCB77', fontSize: '10px' }}>{compressionNote}</span>
                  )}
                  <span style={{ color: '#9A9FA5', fontSize: '10px' }}>{message.fileName}</span>
                </div>
                  );
                })()
              ) : isLegacyStorageUrl(message.mediaUrl) ? (
                <div
                  onClick={() => isOwnMessage && setShowActionsFor((prev) => prev === message.id ? null : message.id)}
                  onContextMenu={(e) => handleImageRightClick(e, message)}
                  style={{ cursor: isOwnMessage ? 'pointer' : 'default' }}
                >
                  <LegacyStorageNotice fileName={message.fileName} />
                </div>
              ) : (
                <div
                  className="message-video-wrapper"
                  onClick={() => setFullscreenVideo({ url: message.mediaUrl, fileName: message.fileName, message })}
                  onContextMenu={(e) => handleImageRightClick(e, message)}
                  style={{ cursor: 'pointer' }}
                  title="Click to play"
                >
                  <video
                    src={`${message.mediaUrl}#t=0.1`}
                    className="message-video"
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedData={handleMediaLoad}
                    onError={() => {
                      console.error('Video failed to load:', message.mediaUrl);
                    }}
                  />
                  <span className="material-symbols-outlined message-video-play">play_circle</span>
                </div>
              )}
              {/* Delete overlay only on mobile; desktop uses context menu */}
              {isOwnMessage && showActionsFor === message.id && isMobile() && (
                <button
                  onClick={requestDelete}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'rgba(225, 29, 72, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '16px'
                  }}
                  title="Delete video"
                >
                  ✕
                </button>
              )}
              </div>
              {reactionsEl}
            </div>
          )}

          {(message.type === 'file' || message.type === 'pdf' || message.type === 'document' || message.type === 'archive') && (
            <div className="bubble-with-reactions">
              <div
                className="message-file"
                style={{ borderColor: rgba(bubbleSoftHex, 0.55), cursor: isOwnMessage ? 'pointer' : 'default' }}
                onClick={() => isOwnMessage && setShowActionsFor((prev) => prev === message.id ? null : message.id)}
              >
              <span 
                className="material-symbols-outlined file-icon" 
                style={{ color: getFileIcon(message.type, message.fileName).color }}
              >
                {getFileIcon(message.type, message.fileName).icon}
              </span>
              <div className="file-info">
                <div className="file-name">{message.fileName}</div>
                <div className="file-size">{formatFileSize(message.fileSize)}</div>
              </div>
              {isLegacyStorageUrl(message.mediaUrl) ? (
                <div className="file-download file-download--disabled" title="File is unavailable">
                  <span className="material-symbols-outlined">block</span>
                </div>
              ) : (
                <a
                  href={message.mediaUrl}
                  download={message.fileName}
                  className="file-download"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="material-symbols-outlined">download</span>
                </a>
              )}
              </div>
              {reactionsEl}
              {isLegacyStorageUrl(message.mediaUrl) ? <LegacyStorageNotice fileName={message.fileName} /> : null}
            </div>
          )}

          {message.type === '3d' && (
            <div className="bubble-with-reactions">
              <div
                className={`message-file message-3d ${expanded3DModels.has(message.id) ? 'expanded' : ''}`}
                style={{ borderColor: rgba(bubbleSoftHex, 0.55), cursor: isOwnMessage ? 'pointer' : 'default' }}
                onClick={() => isOwnMessage && setShowActionsFor((prev) => prev === message.id ? null : message.id)}
              >
              <span 
                className="material-symbols-outlined file-icon" 
                style={{ color: getFileIcon(message.type, message.fileName).color }}
              >
                {getFileIcon(message.type, message.fileName).icon}
              </span>
              <div className="file-info">
                <div className="file-name">{message.fileName}</div>
                <div className="file-size">{formatFileSize(message.fileSize)}</div>
                {message.isPlaceholder && (() => {
                  const prog = message.uploadId ? uploadProgress[message.uploadId] : null;
                  const pct = prog?.percent ?? 0;
                  const label = prog?.stage === 'compressing' ? `Compressing... ${pct}%` : prog?.stage === 'uploading' ? `Uploading... ${pct}%` : 'Uploading...';
                  return (
                  <div className="file-uploading">
                    <span className="uploading-spinner"></span>
                    {label}
                    {prog?.stage && (
                      <div className="upload-progress-bar-track" style={{ width: '100%', marginTop: '4px' }}>
                        <div className="upload-progress-bar-fill" style={{ width: `${pct}%` }}></div>
                      </div>
                    )}
                  </div>
                  );
                })()}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="file-action-btn"
                  onClick={() => toggle3DModel(message.id)}
                  disabled={message.isPlaceholder || isLegacyStorageUrl(message.mediaUrl)}
                  title={
                    message.isPlaceholder
                      ? "Upload in progress..."
                      : isLegacyStorageUrl(message.mediaUrl)
                        ? "Preview unavailable (storage moved)"
                        : (expanded3DModels.has(message.id) ? "Hide 3D Preview" : "Show 3D Preview")
                  }
                  style={{ 
                    opacity: (message.isPlaceholder || isLegacyStorageUrl(message.mediaUrl)) ? 0.5 : 1,
                    cursor: (message.isPlaceholder || isLegacyStorageUrl(message.mediaUrl)) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span className="material-symbols-outlined">
                    {(message.isPlaceholder || isLegacyStorageUrl(message.mediaUrl)) ? 'block' : (expanded3DModels.has(message.id) ? 'visibility_off' : '3d_rotation')}
                  </span>
                </button>
                <a
                  href={(message.isPlaceholder || isLegacyStorageUrl(message.mediaUrl)) ? '#' : message.mediaUrl}
                  download={(message.isPlaceholder || isLegacyStorageUrl(message.mediaUrl)) ? undefined : message.fileName}
                  className="file-download"
                  target="_blank"
                  rel="noopener noreferrer"
                  title={(message.isPlaceholder || isLegacyStorageUrl(message.mediaUrl)) ? "Unavailable" : "Download"}
                  style={{ 
                    opacity: (message.isPlaceholder || isLegacyStorageUrl(message.mediaUrl)) ? 0.5 : 1,
                    pointerEvents: (message.isPlaceholder || isLegacyStorageUrl(message.mediaUrl)) ? 'none' : 'auto'
                  }}
                  onClick={(message.isPlaceholder || isLegacyStorageUrl(message.mediaUrl)) ? (e) => e.preventDefault() : undefined}
                >
                  <span className="material-symbols-outlined">
                    {(message.isPlaceholder || isLegacyStorageUrl(message.mediaUrl)) ? 'block' : 'download'}
                  </span>
                </a>
              </div>
              </div>
              {reactionsEl}
              {isLegacyStorageUrl(message.mediaUrl) ? <LegacyStorageNotice fileName={message.fileName} /> : null}
            </div>
          )}

          {/* Inline 3D Viewer - only show for completed uploads */}
          {message.type === '3d' && !message.isPlaceholder && !isLegacyStorageUrl(message.mediaUrl) && expanded3DModels.has(message.id) && (
            <InlineThreeDViewer
              modelUrl={message.mediaUrl}
              fileName={message.fileName}
              messageId={message.id}
            />
          )}

          {/* Actions for own messages (desktop-only actions moved to context menu) */}
          {isOwnMessage && editingId !== message.id && showActionsFor === message.id && isMobile() && (
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 4 }}>
              <button type="button" className="btn btn-text" onClick={() => startReply(message)}>Reply</button>
              <button type="button" className="btn btn-text" onClick={startEdit}>Edit</button>
              <button type="button" className="btn btn-text" onClick={requestDelete} style={{ color: '#e11d48' }}>Delete</button>
              <button type="button" className="btn btn-text" onClick={() => setShowActionsFor(null)}>Close</button>
            </div>
          )}

          {/* Show timestamp only for last/single messages in group */}
          {showTimestamp && (
            <div className="message-time">
              <TimestampDisplay timestamp={message.timestamp} messageId={message.id} />
            </div>
          )}

          {/* Read-receipt avatars (current read location per user) */}
          {readReceiptUserIds.length > 0 && (
            <SeenByIndicator
              userIds={readReceiptUserIds}
              user={user}
              room={room}
              friends={friends}
              myBubbleColor={myBubbleColor}
              includeSelf={true}
              maxAvatars={5}
            />
          )}

        </div>
      </div>
    );
  };

  // Use server-provided stats (computed from ALL messages) — must be before early returns (React hooks rule)
  const chatStats = useMemo(() => {
    if (!serverStats) return null;
    return {
      ...serverStats,
      firstDate: new Date(serverStats.firstDate),
      lastDate: new Date(serverStats.lastDate),
    };
  }, [serverStats]);

  if (!room) {
    return (
      <div className="chat-window empty">
        <div className="empty-chat">
          <h2>No chat selected</h2>
          <p>Choose from your existing conversations or start a new one</p>
        </div>
      </div>
    );
  }

  // Filter media messages for the panel (newest first)
  const mediaMessages = (messages || [])
    .filter(m => m.type === 'image' || m.type === 'gif' || m.type === 'video')
    .sort((a, b) => b.timestamp - a.timestamp);

  return (
    <div className="chat-container">
      <div
        className="chat-window"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
      {isDragging && (
        <div className="drag-overlay">
          <div className="drag-overlay-content">
            <span className="material-symbols-outlined">upload_file</span>
            <span>Drop files here</span>
          </div>
        </div>
      )}
      {showReconnectBanner && (
        <div className="connection-banner">
          <span className="material-symbols-outlined">wifi_off</span>
          <span>Reconnecting...</span>
        </div>
      )}
      <div className="chat-header">
        <button onClick={onBack} className="back-btn">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <div className="chat-info">
          <div className="chat-details">
            <h3>{room.name}</h3>
            {room.type === 'group' ? (
              (() => {
                const onlineCount = (room.members?.filter(id => id === user.uid || friends?.find(f => f.userId === id)?.online).length) || 0;
                return (
                  <span className={`chat-members ${onlineCount > 0 ? 'online' : ''}`}>
                    {onlineCount} online
                  </span>
                );
              })()
            ) : (
              (() => {
                const otherUserId = room.members?.find(id => id !== user.uid);
                const otherFriend = friends?.find(f => f.userId === otherUserId);
                if (otherFriend?.online) {
                  return <span className="chat-members online">Online</span>;
                }
                return (
                  <LastSeenStatus
                    lastSeen={otherFriend?.lastSeen}
                    platform={otherFriend?.lastSeenPlatform}
                  />
                );
              })()
            )}
          </div>
          {(() => {
            const otherUserId = room.members?.find(id => id !== user.uid);
            const friend = friends?.find(f => f.userId === otherUserId);
            const key = isValidBubbleColor(friend?.bubbleColor) ? friend.bubbleColor : DEFAULT_BUBBLE_COLOR;
            const ring = mixHex(getBubbleHex(key), '#1A1D21', 0.5);
            return (
          <div className="chat-avatar" style={{ borderColor: rgba(ring, 0.6) }}>
            {room.type === 'group' ? (
              safeImageSrc(room.photoURL, null) ? (
                <img 
                  src={safeImageSrc(room.photoURL, null) || undefined}
                  alt={room.name}
                  className="group-photo"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    // If group photo is broken/unavailable, hide it and show the default "G" icon.
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '';
                  }}
                />
              ) : (
                <span className="group-icon">G</span>
              )
            ) : (
                  <div className="dm-avatar-container">
                <img 
                  src={safeImageSrc(room.otherUserPhoto, DEFAULT_AVATAR, room.name)}
                  alt={room.name}
                  className="direct-message-avatar"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = getDefaultAvatarWithInitial(room.name);
                  }}
                />
                {(() => {
                  const otherUserId = room.members?.find(id => id !== user.uid);
                  const friend = friends?.find(f => f.userId === otherUserId);
                  if (!friend?.online) return null;
                  const platform = friend.platform || 'web';
                  const isMobile = platform === 'mobile';
                  return (
                    <div className="presence-indicator">
                      <div className="presence-dot-simple" />
                      <span className="presence-platform">{isMobile ? 'Mob' : 'PC'}</span>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
            );
          })()}
        </div>
        <div className="chat-header-actions">
          <button onClick={() => { setShowSearch(s => !s); setTimeout(() => searchInputRef.current?.focus(), 100); }} className="chat-header-action-btn" title="Search messages (Ctrl+F)">
            <span className="material-symbols-outlined">search</span>
          </button>
          {room.type === 'group' && (
            <button onClick={() => setShowGroupSettings(true)} className="chat-header-action-btn" title="Group Settings">
              <span className="material-symbols-outlined">settings</span>
            </button>
          )}
        </div>
      </div>

      {showSearch && (
        <div className="search-panel">
          <div className="search-panel-input-row">
            <span className="material-symbols-outlined search-panel-icon">search</span>
            <input
              ref={searchInputRef}
              type="text"
              className="search-panel-input"
              placeholder={room ? `Search in ${room.name}...` : 'Search all chats...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            {searchQuery && (
              <button className="search-panel-clear" onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}>
                <span className="material-symbols-outlined">close</span>
              </button>
            )}
            <button className="search-panel-close" onClick={() => { setShowSearch(false); setSearchQuery(''); setSearchResults([]); }}>
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          {searchQuery.trim().length >= 2 && (
            <div className="search-panel-results">
              {searchLoading ? (
                <div className="search-panel-status">Searching...</div>
              ) : searchResults.length === 0 ? (
                <div className="search-panel-status">No results found</div>
              ) : (
                <>
                  <div className="search-panel-count">{searchTotal} result{searchTotal !== 1 ? 's' : ''}</div>
                  {searchResults.map((result) => (
                    <div key={result.id} className="search-result-item" onClick={() => handleSearchResultClick(result)}>
                      <div className="search-result-header">
                        <span className="search-result-sender">{result.senderName}</span>
                        {result.roomName && result.roomName !== room?.name && (
                          <span className="search-result-room">in {result.roomName}</span>
                        )}
                        <span className="search-result-time">
                          <TimestampDisplay timestamp={result.timestamp} messageId={result.id} />
                        </span>
                      </div>
                      <div className="search-result-content">{result.content}</div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="messages-container" ref={messagesContainerRef}>
        {messages.length === 0 ? (
          <div className="no-messages">
            {!isConnected ? (
              <>
                <p>Connecting...</p>
                <p style={{ color: '#9A9FA5', fontSize: 12, marginTop: 6 }}>Hang tight while we reconnect.</p>
              </>
            ) : isLoadingMessages ? (
              <div className="skeleton-messages">
                {[0.6, 0.4, 0.75, 0.35, 0.55].map((w, i) => (
                  <div key={i} className={`skeleton-message ${i % 2 === 0 ? 'other' : 'own'}`}>
                    {i % 2 === 0 && <div className="skeleton-avatar" />}
                    <div className="skeleton-bubble" style={{ width: `${w * 100}%` }} />
                  </div>
                ))}
              </div>
            ) : (
              <p>No messages yet. Start the conversation!</p>
            )}
          </div>
        ) : (
          messages.map((message, index) => renderMessage(message, index, messages))
        )}
        
        {typingUsers.size > 0 && (
          <div className="typing-indicator">
            <div className="typing-text">
              {Array.from(typingUsers.keys()).join(', ')}{' '}
              {typingUsers.size === 1 ? 'is' : 'are'} typing... (
              {Array.from(typingUsers.values()).join(', ')})
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className="message-input-container" style={{ position: 'relative' }}>
        {replyTo && (
          <div className="reply-composer-banner" onClick={(e) => e.stopPropagation()}>
            <div className="reply-composer-text">
              <div className="reply-composer-title">Replying to {replyTo.senderName}</div>
              <div className="reply-composer-preview">{replyTo.content}</div>
            </div>
            {replyTo.mediaUrl && ['image', 'gif', 'video'].includes(replyTo.type) && (
              replyTo.type === 'video' ? (
                <video src={replyTo.mediaUrl} className="reply-composer-thumb" muted />
              ) : (
                <img src={replyTo.mediaUrl} alt="" className="reply-composer-thumb" />
              )
            )}
            <button
              type="button"
              className="reply-composer-close"
              onClick={() => setReplyTo(null)}
              title="Cancel reply"
            >
              ✕
            </button>
          </div>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept="*"
          multiple
        />
        <input
          type="file"
          ref={cameraInputRef}
          onChange={async (e) => {
            await handleFileSelect(e);
            if (cameraInputRef.current) cameraInputRef.current.value = '';
          }}
          style={{ display: 'none' }}
          accept="image/*,video/*"
          capture="environment"
        />

        {room?.type !== 'project' && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="attach-btn"
          disabled={uploading || activeUploads.size > 0}
          title="Attach file"
        >
          {uploading || activeUploads.size > 0 ? (
            <span className="material-symbols-outlined">progress_activity</span>
          ) : (
            <span className="material-symbols-outlined">attach_file</span>
          )}
        </button>
        )}

        {room?.type !== 'project' && (
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          className="camera-btn"
          disabled={uploading || activeUploads.size > 0}
          title="Take photo or video"
        >
          <span className="material-symbols-outlined">photo_camera</span>
        </button>
        )}

        <div
          ref={messageInputRef}
          contentEditable={!uploading && activeUploads.size === 0}
          onInput={handleContentEditableInput}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(e);
            }
          }}
          onPaste={handlePaste}
          data-placeholder="Type a message..."
          className="message-input contenteditable-input"
          role="textbox"
          aria-multiline="true"
        />

        <button
          type="submit"
          className="send-btn"
          disabled={!messageText.trim() || uploading || activeUploads.size > 0}
        >
          <span className="material-symbols-outlined">arrow_upward</span>
        </button>
      </form>

      {/* Desktop Reaction Picker (floating) */}
      {reactionMenu && !showEmojiPicker && (
        <div
          className="reaction-popover"
          ref={reactionPickerRef}
          style={{
            position: 'fixed',
            left: `${reactionMenu.x}px`,
            top: `${reactionMenu.y}px`,
            zIndex: 2000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="reaction-popover__row" role="group" aria-label="Quick reactions">
            {getQuickReactions().map((emoji) => (
              <button
                key={emoji}
                className="reaction-option"
                onClick={() => handleReactionClick(reactionMenu.messageId, emoji)}
                type="button"
              >
                {emoji}
              </button>
            ))}
            <button
              className="reaction-option reaction-option--more"
              title="More emojis"
              onClick={() => setShowEmojiPicker(true)}
              type="button"
            >
              ⋯
            </button>
          </div>

          <button
            className="reaction-popover__reply"
            type="button"
            onClick={() => {
              const m = (messages || []).find(mm => mm.id === reactionMenu.messageId);
              startReply(m);
              setReactionMenu(null);
              setShowEmojiPicker(false);
            }}
          >
            <span className="material-symbols-outlined">reply</span>
            Reply
          </button>
        </div>
      )}

      {/* Full Emoji Picker */}
      {showEmojiPicker && (
        <div
          className={`emoji-picker ${isMobile() ? 'mobile' : 'desktop'}`}
          ref={emojiPickerRef}
          style={isMobile() ? {} : {
            position: 'fixed',
            left: `${reactionMenu?.x || 0}px`,
            top: `${reactionMenu?.y || 0}px`,
            zIndex: 2100
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="emoji-picker-header">
            <div className="emoji-picker-title">React</div>
            <div className="emoji-picker-actions">
              <button
                className="emoji-reply-btn"
                type="button"
                onClick={() => {
                  const targetId = pickerMessageId || reactionMenu?.messageId;
                  const m = (messages || []).find(mm => mm.id === targetId);
                  startReply(m);
                  setShowEmojiPicker(false);
                  setReactionMenu(null);
                }}
                title="Reply"
              >
                <span className="material-symbols-outlined">reply</span>
                Reply
              </button>
              <button
                className="emoji-close-btn"
                type="button"
                onClick={() => { setShowEmojiPicker(false); setReactionMenu(null); }}
                title="Close"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
          </div>

          <div className="emoji-picker-content">
            <EmojiPicker
              theme="dark"
              skinTonesDisabled={false}
              searchDisabled={false}
              previewConfig={{ showPreview: false }}
              height="100%"
              width="100%"
              onEmojiClick={(emojiData) => {
                const targetId = pickerMessageId || reactionMenu?.messageId;
                if (!targetId) return;
                handleReactionClick(targetId, emojiData.emoji);
              }}
            />
          </div>
        </div>
      )}

      {/* Image/Media Context Menu (desktop) - own media only */}
      {imageContextMenu && !isMobile() && (
        <div
          className="image-context-menu"
          ref={imageContextMenuRef}
          style={{
            position: 'fixed',
            left: `${imageContextMenu.x}px`,
            top: `${imageContextMenu.y}px`,
            zIndex: 2000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Removed mini reaction row; use full emoji picker for others */}
          <button
            className="context-menu-item"
            onClick={() => downloadImage(imageContextMenu.mediaUrl, imageContextMenu.fileName, imageContextMenu.messageType)}
          >
            <span className="material-symbols-outlined">file_download</span>
            Download {imageContextMenu.messageType === 'video' ? 'Video' : 
                     imageContextMenu.messageType === 'gif' ? 'GIF' : 'Image'}
          </button>
          {(() => {
            const message = messages.find(m => m.id === imageContextMenu.messageId);
            const isOwn = message && message.senderId === user.uid;
            return isOwn ? (
              <button
                className="context-menu-item"
                onClick={() => {
                  const socket = getSocket();
                  if (!socket || !isSocketConnected()) return;
                  if (!window.confirm('Delete this message for everyone?')) return;
                  socket.emit('message:delete', { roomId: room.roomId, messageId: imageContextMenu.messageId });
                  setImageContextMenu(null);
                }}
                style={{ color: '#e11d48' }}
              >
                <span className="material-symbols-outlined">delete</span>
                Delete Message
              </button>
            ) : null;
          })()}
        </div>
      )}

      {/* Text Context Menu (desktop) */}
      {textContextMenu && !isMobile() && (
        <div
          className="image-context-menu"
          ref={textContextMenuRef}
          style={{
            position: 'fixed',
            left: `${textContextMenu.x}px`,
            top: `${textContextMenu.y}px`,
            zIndex: 2000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="context-menu-item"
            onClick={() => {
              const message = messages.find(m => m.id === textContextMenu.messageId);
              if (!message) return;
              startReply(message);
              setTextContextMenu(null);
            }}
          >
            <span className="material-symbols-outlined">reply</span>
            Reply
          </button>
          <button
            className="context-menu-item"
            onClick={() => {
              const message = messages.find(m => m.id === textContextMenu.messageId);
              if (!message) return;
              setEditingId(message.id);
              setEditDraft(message.content || '');
              setTextContextMenu(null);
            }}
          >
            <span className="material-symbols-outlined">edit</span>
            Edit Message
          </button>
          <button
            className="context-menu-item"
            onClick={() => {
              const message = messages.find(m => m.id === textContextMenu.messageId);
              if (!message || !room) return;
              const socket = getSocket();
              if (!socket || !isSocketConnected()) return;
              if (!window.confirm('Delete this message for everyone?')) return;
              socket.emit('message:delete', { roomId: room.roomId, messageId: message.id });
              setTextContextMenu(null);
            }}
            style={{ color: '#e11d48' }}
          >
            <span className="material-symbols-outlined">delete</span>
            Delete Message
          </button>
        </div>
      )}

      {/* Compression Dialog */}
      {pendingCompressFiles && (
        <div className="compression-dialog-overlay" onClick={() => { setPendingCompressFiles(null); if (pendingCompressFiles.source === 'select' && fileInputRef.current) fileInputRef.current.value = ''; }}>
          <div className="compression-dialog" onClick={e => e.stopPropagation()}>
            <div className="compression-dialog-header">
              <h3>Send {pendingCompressFiles.filesWithTypes.length > 1 ? 'Files' : pendingCompressFiles.filesWithTypes[0]?.fileType === 'video' ? 'Video' : 'Image'}</h3>
              <button className="close-btn" onClick={() => { setPendingCompressFiles(null); if (pendingCompressFiles.source === 'select' && fileInputRef.current) fileInputRef.current.value = ''; }}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="compression-dialog-content">
              <p>How would you like to send {pendingCompressFiles.filesWithTypes.length > 1 ? 'these files' : 'this file'}?</p>
              {pendingCompressFiles.filesWithTypes.filter(({ fileType }) => ['image', 'video'].includes(fileType)).map(({ file, fileType }, i) => (
                <div className="file-info" key={i}>
                  <span className="material-symbols-outlined">{fileType === 'video' ? 'videocam' : 'image'}</span>
                  <div>
                    <div className="file-name">{file.name || 'Pasted image'}</div>
                    <div className="file-size">{formatFileSize(file.size)}</div>
                  </div>
                </div>
              ))}
              <p className="compression-dialog-note">Compressed files are faster to send and use less storage.</p>
            </div>
            <div className="compression-dialog-actions">
              <button className="dialog-btn full-res-btn" onClick={() => handleCompressionChoice(false)}>
                <span className="material-symbols-outlined">high_quality</span>
                Full Resolution
              </button>
              <button className="dialog-btn compressed-btn" onClick={() => handleCompressionChoice(true)}>
                <span className="material-symbols-outlined">compress</span>
                Compressed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div
          className="fullscreen-modal"
          onClick={() => {
            setFullscreenImage(null);
            setZoomScale(1);
            setZoomPosition({ x: 0, y: 0 });
            setFullscreenMetadata({});
            setIsHoveringFullscreen(false);
            setShowFullscreenMetadata(false);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            cursor: 'pointer',
            overflow: 'hidden'
          }}
        >
          <img
            ref={fullscreenImageRef}
            src={fullscreenImage.url}
            alt="Fullscreen"
            loading="eager"
            decoding="async"
            style={{
              maxWidth: zoomScale === 1 ? '90%' : 'none',
              maxHeight: zoomScale === 1 ? '90%' : 'none',
              width: zoomScale !== 1 ? `${zoomScale * 90}%` : undefined,
              borderRadius: '8px',
              objectFit: 'contain',
              transform: `scale(${zoomScale}) translate(${zoomPosition.x}px, ${zoomPosition.y}px)`,
              transformOrigin: 'center center',
              transition: zoomScale === 1 ? 'transform 0.3s ease' : 'none',
              touchAction: 'none',
              userSelect: 'none'
            }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => {
              e.stopPropagation();
              const touches = e.touches;
              if (touches.length === 2) {
                // Store initial pinch distance
                const touch1 = touches[0];
                const touch2 = touches[1];
                const distance = Math.sqrt(
                  Math.pow(touch2.clientX - touch1.clientX, 2) + 
                  Math.pow(touch2.clientY - touch1.clientY, 2)
                );
                fullscreenImageRef.current.initialPinchDistance = distance;
                fullscreenImageRef.current.initialScale = zoomScale;
              } else if (touches.length === 1 && zoomScale > 1) {
                // Store initial pan position
                fullscreenImageRef.current.lastTouchX = touches[0].clientX;
                fullscreenImageRef.current.lastTouchY = touches[0].clientY;
              }
            }}
            onTouchMove={(e) => {
              e.stopPropagation();
              e.preventDefault();
              const touches = e.touches;
              if (touches.length === 2 && fullscreenImageRef.current.initialPinchDistance) {
                // Calculate new pinch distance
                const touch1 = touches[0];
                const touch2 = touches[1];
                const distance = Math.sqrt(
                  Math.pow(touch2.clientX - touch1.clientX, 2) + 
                  Math.pow(touch2.clientY - touch1.clientY, 2)
                );
                const scale = (distance / fullscreenImageRef.current.initialPinchDistance) * fullscreenImageRef.current.initialScale;
                setZoomScale(Math.max(1, Math.min(5, scale)));
              } else if (touches.length === 1 && zoomScale > 1 && fullscreenImageRef.current.lastTouchX) {
                // Pan the image
                const deltaX = touches[0].clientX - fullscreenImageRef.current.lastTouchX;
                const deltaY = touches[0].clientY - fullscreenImageRef.current.lastTouchY;
                setZoomPosition(prev => ({
                  x: prev.x + deltaX / zoomScale,
                  y: prev.y + deltaY / zoomScale
                }));
                fullscreenImageRef.current.lastTouchX = touches[0].clientX;
                fullscreenImageRef.current.lastTouchY = touches[0].clientY;
              }
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              if (e.touches.length < 2) {
                fullscreenImageRef.current.initialPinchDistance = null;
              }
              if (e.touches.length === 0) {
                fullscreenImageRef.current.lastTouchX = null;
                fullscreenImageRef.current.lastTouchY = null;
              }
            }}
            onWheel={(e) => {
              e.stopPropagation();
              e.preventDefault();
              const delta = e.deltaY * -0.01;
              const newScale = Math.max(1, Math.min(5, zoomScale + delta));
              setZoomScale(newScale);
              if (newScale === 1) {
                setZoomPosition({ x: 0, y: 0 });
              }
            }}
            onLoad={(e) => {
              // Store image dimensions for metadata
              const img = e.target;
              if (fullscreenImage.message) {
                setFullscreenMetadata(prev => ({
                  ...prev,
                  width: img.naturalWidth,
                  height: img.naturalHeight
                }));
              }
            }}
          />
          
          {/* Info Button */}
          <button
            className="fullscreen-info-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowFullscreenMetadata(prev => !prev);
            }}
            title="Toggle info"
          >
            <span className="material-symbols-outlined">info</span>
          </button>
          
          {fullscreenImage.message && showFullscreenMetadata && (
            <div className="fullscreen-metadata" onClick={(e) => e.stopPropagation()}>
              <div className="metadata-row">
                <span className="metadata-label">Received:</span>
                <span className="metadata-value">{formatDate(fullscreenImage.message.timestamp)}</span>
              </div>
              <div className="metadata-row">
                <span className="metadata-label">Dimensions:</span>
                <span className="metadata-value">
                  {fullscreenMetadata.width && fullscreenMetadata.height
                    ? `${fullscreenMetadata.width} × ${fullscreenMetadata.height}`
                    : 'Loading...'}
                </span>
              </div>
              <div className="metadata-row">
                <span className="metadata-label">File Size:</span>
                <span className="metadata-value">{fullscreenImage.message.fileSize ? formatFileSize(fullscreenImage.message.fileSize) : 'Unknown'}</span>
              </div>
              <div className="metadata-row">
                <span className="metadata-label">File Name:</span>
                <span className="metadata-value">{fullscreenImage.message.fileName || 'Unknown'}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fullscreen Video Modal */}
      {fullscreenVideo && (
        <div
          className="fullscreen-modal"
          onClick={() => {
            setFullscreenVideo(null);
            setFullscreenMetadata({});
            setIsHoveringFullscreen(false);
            setShowFullscreenMetadata(false);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            cursor: 'pointer',
            overflow: 'hidden'
          }}
        >
          <video
            className="fullscreen-video"
            src={fullscreenVideo.url}
            controls
            autoPlay
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              borderRadius: '8px',
              objectFit: 'contain',
              background: '#000'
            }}
            onClick={(e) => e.stopPropagation()}
            onLoadedMetadata={(e) => {
              // Store video dimensions and duration for metadata
              const video = e.target;
              if (fullscreenVideo.message) {
                setFullscreenMetadata(prev => ({
                  ...prev,
                  width: video.videoWidth,
                  height: video.videoHeight,
                  duration: video.duration
                }));
              }
            }}
          />
          
          {/* Info Button */}
          <button
            className="fullscreen-info-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowFullscreenMetadata(prev => !prev);
            }}
            title="Toggle info"
          >
            <span className="material-symbols-outlined">info</span>
          </button>
          
          {fullscreenVideo.message && showFullscreenMetadata && (
            <div className="fullscreen-metadata" onClick={(e) => e.stopPropagation()}>
              <div className="metadata-row">
                <span className="metadata-label">Received:</span>
                <span className="metadata-value">{formatDate(fullscreenVideo.message.timestamp)}</span>
              </div>
              <div className="metadata-row">
                <span className="metadata-label">Dimensions:</span>
                <span className="metadata-value">
                  {fullscreenMetadata.width && fullscreenMetadata.height
                    ? `${fullscreenMetadata.width} × ${fullscreenMetadata.height}`
                    : 'Loading...'}
                </span>
              </div>
              <div className="metadata-row">
                <span className="metadata-label">Duration:</span>
                <span className="metadata-value">{fullscreenMetadata.duration ? formatVideoDuration(fullscreenMetadata.duration) : 'Loading...'}</span>
              </div>
              <div className="metadata-row">
                <span className="metadata-label">File Size:</span>
                <span className="metadata-value">{fullscreenVideo.message.fileSize ? formatFileSize(fullscreenVideo.message.fileSize) : 'Unknown'}</span>
              </div>
              <div className="metadata-row">
                <span className="metadata-label">File Name:</span>
                <span className="metadata-value">{fullscreenVideo.message.fileName || fullscreenVideo.fileName || 'Unknown'}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {showGroupSettings && room.type === 'group' && (
        <GroupSettings
          room={room}
          user={user}
          onClose={() => setShowGroupSettings(false)}
        />
      )}

      {/* 3D Model Viewer */}
      {show3DViewer && (
        <ThreeDViewer
          modelUrl={show3DViewer.url}
          fileName={show3DViewer.fileName}
          onClose={() => setShow3DViewer(null)}
        />
      )}

      {/* Tenor GIF Picker */}
      {showGifPicker && (
        <TenorGifPicker
          onSelectGif={handleGifSelect}
          onClose={() => setShowGifPicker(false)}
        />
      )}

      {hasMoreNewer ? (
        <button
          type="button"
          className="jump-to-bottom has-newer"
          onClick={() => onReturnToLatest?.(room?.roomId)}
          title="Back to the latest messages"
        >
          <span className="material-symbols-outlined">south</span>
          <span className="jump-to-bottom-label">Jump to latest</span>
        </button>
      ) : !shouldAutoScroll && (
        <button
          type="button"
          className="jump-to-bottom"
          onClick={scrollToBottom}
          title="Jump to bottom"
        >
          <span className="material-symbols-outlined">south</span>
        </button>
      )}

    </div>

    {/* Media Panel (Web only) — hidden inside project chat */}
    {room?.type !== 'project' && <div className={`media-panel ${showMediaPanel ? 'open' : ''}`}>
      <div className="media-panel-header">
        <button
          className="media-panel-toggle"
          onClick={() => setShowMediaPanel(!showMediaPanel)}
          title={showMediaPanel ? "Hide panel" : "Show panel"}
        >
          <span className="material-symbols-outlined">
            {showMediaPanel ? 'chevron_right' : 'chevron_left'}
          </span>
        </button>
        {showMediaPanel && (
          <div className="media-panel-tabs">
            <button className={`media-panel-tab ${mediaPanelTab === 'media' ? 'active' : ''}`} onClick={() => setMediaPanelTab('media')}>
              <span className="material-symbols-outlined">photo_library</span>
              <span>Media</span>
            </button>
            <button className={`media-panel-tab ${mediaPanelTab === 'stats' ? 'active' : ''}`} onClick={() => setMediaPanelTab('stats')}>
              <span className="material-symbols-outlined">bar_chart</span>
              <span>Stats</span>
            </button>
          </div>
        )}
      </div>

      {showMediaPanel && mediaPanelTab === 'media' && (
        <div className="media-panel-content" ref={mediaPanelContentRef}>
          {mediaMessages.length === 0 ? (
            <div className="media-panel-empty">
              <span className="material-symbols-outlined">photo_library</span>
              <p>No media yet</p>
            </div>
          ) : (
            <div className="media-grid">
              {mediaMessages.map((msg) => {
                const isMedia = msg.type === 'image' || msg.type === 'gif';
                const isVideo = msg.type === 'video';
                const isFile = msg.type === 'file' || msg.type === 'pdf' || msg.type === 'document' || msg.type === 'archive' || msg.type === '3d';

                return (
                  <div key={msg.id} className="media-item">
                    {isMedia && !isLegacyStorageUrl(msg.mediaUrl) && (
                      <>
                        <img
                          src={msg.mediaUrl}
                          alt={msg.fileName || 'Image'}
                          onClick={() => setFullscreenImage({ url: msg.mediaUrl, message: msg })}
                          className="media-thumbnail"
                        />
                        <div className="media-date-overlay">
                          {formatDateShort(msg.timestamp)}
                        </div>
                        <img
                          src={safeImageSrc(msg.senderPhoto, DEFAULT_AVATAR, msg.senderName)}
                          alt={msg.senderName || 'User'}
                          className="media-uploader-avatar"
                          title={`Uploaded by ${msg.senderName || 'User'}`}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = getDefaultAvatarWithInitial(msg.senderName || 'U');
                          }}
                        />
                      </>
                    )}
                    {isVideo && !isLegacyStorageUrl(msg.mediaUrl) && (
                      <div className="media-thumbnail video-thumbnail" onClick={() => setFullscreenVideo({ url: msg.mediaUrl, fileName: msg.fileName, message: msg })}>
                        <video src={msg.mediaUrl} />
                        <span className="material-symbols-outlined play-icon">play_circle</span>
                        <div className="media-date-overlay">
                          {formatDateShort(msg.timestamp)}
                        </div>
                        <img
                          src={safeImageSrc(msg.senderPhoto, DEFAULT_AVATAR, msg.senderName)}
                          alt={msg.senderName || 'User'}
                          className="media-uploader-avatar"
                          title={`Uploaded by ${msg.senderName || 'User'}`}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = getDefaultAvatarWithInitial(msg.senderName || 'U');
                          }}
                        />
                      </div>
                    )}
                    {isFile && (
                      <div className="file-item-thumbnail">
                        <span className="material-symbols-outlined file-icon" style={{ color: getFileIcon(msg.type, msg.fileName).color }}>
                          {getFileIcon(msg.type, msg.fileName).icon}
                        </span>
                        <span className="file-item-name">{msg.fileName}</span>
                        <img
                          src={safeImageSrc(msg.senderPhoto, DEFAULT_AVATAR, msg.senderName)}
                          alt={msg.senderName || 'User'}
                          className="media-uploader-avatar file-uploader-avatar"
                          title={`Uploaded by ${msg.senderName || 'User'}`}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = getDefaultAvatarWithInitial(msg.senderName || 'U');
                          }}
                        />
                      </div>
                    )}
                    {isLegacyStorageUrl(msg.mediaUrl) && (
                      <div className="media-unavailable">
                        <span className="material-symbols-outlined">block</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Load More Button */}
          {mediaMessages.length > 0 && hasMoreOlder && !loadingOlder && (
            <button
              className="load-more-media-btn"
              onClick={() => {
                const oldest = mediaMessages[mediaMessages.length - 1];
                const beforeTs = oldest?.timestamp;
                if (beforeTs && typeof onLoadOlderMessages === 'function') {
                  onLoadOlderMessages(room?.roomId, beforeTs);
                }
              }}
            >
              <span className="material-symbols-outlined">expand_more</span>
              Load More
            </button>
          )}

          {loadingOlder && (
            <div className="loading-more-media">
              <span className="loading-spinner"></span>
              <span>Loading...</span>
            </div>
          )}
        </div>
      )}

      {/* Stats Tab */}
      {showMediaPanel && mediaPanelTab === 'stats' && (
        <div className="media-panel-content stats-panel-content">
          {statsLoading ? (
            <div className="media-panel-empty">
              <span className="material-symbols-outlined">hourglass_top</span>
              <p>Loading stats...</p>
            </div>
          ) : !chatStats ? (
            <div className="media-panel-empty">
              <span className="material-symbols-outlined">bar_chart</span>
              <p>No messages yet</p>
            </div>
          ) : (
            <div className="stats-container">
              {/* Overview */}
              <div className="stats-section">
                <div className="stats-big-number">
                  <span className="stats-value">{chatStats.totalMessages.toLocaleString()}</span>
                  <span className="stats-label">total messages</span>
                </div>
                <div className="stats-row">
                  <div className="stats-mini">
                    <span className="stats-mini-value">{chatStats.avgPerDay}</span>
                    <span className="stats-mini-label">per day</span>
                  </div>
                  <div className="stats-mini">
                    <span className="stats-mini-value">{chatStats.daySpan}</span>
                    <span className="stats-mini-label">days</span>
                  </div>
                </div>
              </div>

              {/* Who talks most */}
              <div className="stats-section">
                <h4 className="stats-section-title">Who talks most</h4>
                {chatStats.userStats.map((u, i) => {
                  const pct = Math.round((u.count / chatStats.totalMessages) * 100);
                  const maxCount = chatStats.userStats[0].count;
                  const barWidth = Math.round((u.count / maxCount) * 100);
                  return (
                    <div key={u.userId || i} className="stats-user-row">
                      <img
                        src={safeImageSrc(u.photo, DEFAULT_AVATAR, u.name)}
                        alt={u.name}
                        className="stats-user-avatar"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = getDefaultAvatarWithInitial(u.name || 'U');
                        }}
                      />
                      <div className="stats-user-info">
                        <div className="stats-user-name-row">
                          <span className="stats-user-name">{u.name}</span>
                          <span className="stats-user-count">{u.count.toLocaleString()} ({pct}%)</span>
                        </div>
                        <div className="stats-bar-track">
                          <div className="stats-bar-fill" style={{ width: `${barWidth}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Activity */}
              <div className="stats-section">
                <h4 className="stats-section-title">Activity</h4>
                <div className="stats-activity-grid">
                  <div className="stats-activity-card">
                    <span className="material-symbols-outlined">schedule</span>
                    <span className="stats-activity-value">{chatStats.busiestHour}</span>
                    <span className="stats-activity-label">busiest hour</span>
                  </div>
                  <div className="stats-activity-card">
                    <span className="material-symbols-outlined">calendar_today</span>
                    <span className="stats-activity-value">{chatStats.busiestDay}</span>
                    <span className="stats-activity-label">busiest day</span>
                  </div>
                </div>
                {/* Hour chart */}
                <div className="stats-hour-chart">
                  {chatStats.hourCounts.map((count, h) => {
                    const maxH = Math.max(...chatStats.hourCounts);
                    const height = maxH > 0 ? Math.max(2, Math.round((count / maxH) * 40)) : 2;
                    return (
                      <div key={h} className="stats-hour-bar-wrap" title={`${h}:00 — ${count} msgs`}>
                        <div className="stats-hour-bar" style={{ height: `${height}px` }} />
                        {h % 6 === 0 && <span className="stats-hour-label">{h}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Media breakdown */}
              <div className="stats-section">
                <h4 className="stats-section-title">Shared</h4>
                <div className="stats-media-grid">
                  {chatStats.photoCount > 0 && (
                    <div className="stats-media-chip">
                      <span className="material-symbols-outlined">photo</span>
                      <span>{chatStats.photoCount} photos</span>
                    </div>
                  )}
                  {chatStats.videoCount > 0 && (
                    <div className="stats-media-chip">
                      <span className="material-symbols-outlined">videocam</span>
                      <span>{chatStats.videoCount} videos</span>
                    </div>
                  )}
                  {chatStats.gifCount > 0 && (
                    <div className="stats-media-chip">
                      <span className="material-symbols-outlined">gif_box</span>
                      <span>{chatStats.gifCount} GIFs</span>
                    </div>
                  )}
                  {chatStats.fileCount > 0 && (
                    <div className="stats-media-chip">
                      <span className="material-symbols-outlined">attach_file</span>
                      <span>{chatStats.fileCount} files</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Top reactions */}
              {chatStats.topReactions.length > 0 && (
                <div className="stats-section">
                  <h4 className="stats-section-title">Top reactions</h4>
                  <div className="stats-reactions-row">
                    {chatStats.topReactions.map(([emoji, count]) => (
                      <div key={emoji} className="stats-reaction-chip">
                        <span className="stats-reaction-emoji">{emoji}</span>
                        <span className="stats-reaction-count">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Chat started */}
              <div className="stats-section stats-footer">
                <span className="stats-footer-text">
                  Chat started {chatStats.firstDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>}

  </div>
  );
}

export default ChatWindow;

